// ===== NEW FEATURE START ===== (imports for related-calculators / learning-links plugin, see bottom of file)
import relatedCalculators from './relatedCalculators';
import learningLinks from './learningLinks';
// ===== NEW FEATURE END =====

const SPELL_MAP = {
    // Currency
    doler: 'dollar', dolor: 'dollar', dollor: 'dollar', dolar: 'dollar',
    rupes: 'rupees', rupess: 'rupees', ruppe: 'rupee', rupe: 'rupee',
    // Common calc terms
    gstt: 'gst', emii: 'emi', percantage: 'percentage', percenatge: 'percentage',
    percentag: 'percentage', persentage: 'percentage', persent: 'percent',
    intrest: 'interest', interst: 'interest', intreset: 'interest',
    loann: 'loan', lone: 'loan',
    celcius: 'celsius', celsuis: 'celsius', farenheit: 'fahrenheit', farhenheit: 'fahrenheit',
    kilogrms: 'kilograms', kilograme: 'kilogram', kilomter: 'kilometer', kilomtr: 'kilometer',
    disount: 'discount', discont: 'discount', discunt: 'discount',
    compund: 'compound', compaund: 'compound',
    calulate: 'calculate', caculate: 'calculate', calcuate: 'calculate',
    calulator: 'calculator',
    profitt: 'profit', margine: 'margin', markkup: 'markup',
    factoriall: 'factorial',
};

const SYNONYM_MAP = [
    // Currency codes
    [/\b(us\s?dollars?|american\s?dollars?|\$)\b/gi, 'usd'],
    [/\b(indian\s?rupees?|rs\.?|inr)\b/gi, 'inr'],
    [/\b(euros?|€)\b/gi, 'eur'],
    [/\b(british\s?pounds?|pounds?\s?sterling|gbp|£)\b/gi, 'gbp'],
    [/\b(japanese\s?yen|¥|yen)\b/gi, 'jpy'],
    // Calc terms
    [/\b(percent(age)?s?)\b/gi, 'percent'],
    [/\b(good\s+and\s+services\s+tax)\b/gi, 'gst'],
    [/\b(equated\s+monthly\s+instal?ments?)\b/gi, 'emi'],
    [/\b(recurring\s+deposits?)\b/gi, 'rd'],
    [/\b(fixed\s+deposits?)\b/gi, 'fd'],
    [/\b(systematic\s+investment\s+plans?)\b/gi, 'sip'],
    [/\b(cost\s+to\s+company)\b/gi, 'ctc'],
    [/\b(body\s+mass\s+index)\b/gi, 'bmi'],
    [/\b(basal\s+metabolic\s+rate)\b/gi, 'bmr'],
    [/\b(what's|whats)\b/gi, 'what is'],
    [/\b(how's|hows)\b/gi, 'how is'],
];

function spellCorrect(str) {
    return str.replace(/\b([a-z]+)\b/gi, (m) => {
        const low = m.toLowerCase();
        return SPELL_MAP[low] || m;
    });
}

function expandSynonyms(str) {
    let out = str;
    SYNONYM_MAP.forEach(([re, rep]) => { out = out.replace(re, rep); });
    return out;
}

/* ---------------------------------------------------------------
   LAYER 3 : NORMALIZATION (Hinglish + Indian scale + number cleanup)
   --------------------------------------------------------------- */
const HINGLISH_MAP = [
    [/\bka\b/g, 'of'], [/\bki\b/g, 'of'], [/\bke\b/g, 'of'],
    [/\bnikalo\b/g, 'calculate'], [/\bnikaliye\b/g, 'calculate'], [/\bnikal do\b/g, 'calculate'],
    [/\bbatao\b/g, 'tell'], [/\bbataiye\b/g, 'tell'], [/\bbata do\b/g, 'tell'],
    [/\bkitna hai\b/g, 'how much is'], [/\bkitni hai\b/g, 'how much is'],
    [/\bkitna hoga\b/g, 'how much is'], [/\bkitni hogi\b/g, 'how much is'],
    [/\bkitna\b/g, 'how much'], [/\bkitni\b/g, 'how much'], [/\bkitne\b/g, 'how many'],
    [/\bkaro\b/g, ''], [/\bkijiye\b/g, ''], [/\bkar do\b/g, ''], [/\bkar den\b/g, ''],
    [/\blagayen\b/g, 'apply'], [/\bjodo\b/g, 'add'], [/\bghatao\b/g, 'subtract'],
    [/\bguna\b/g, 'multiply'], [/\bbhag\b/g, 'divide'],
    [/\brupaye\b/g, 'rupees'], [/\brupee\b/g, 'rupees'], [/\bpaisa\b/g, 'money'],
    [/\bumar\b/g, 'age'], [/\bumr\b/g, 'age'], [/\bsaal\b/g, 'years'], [/\bmahine\b/g, 'months'],
    [/\bmahina\b/g, 'month'], [/\b(aaj|aj|ajj)\b/g, 'today'],
    [/\b(din|dino)\b/g, 'days'], [/\b(baad|bad)\b/g, 'after'], [/\bpehle\b/g, 'before'],
    [/\b(hoga|hogi|hongi)\b/g, ''], [/\b(konsi|kaunsi|kaunsa)\b/g, 'which'],
    [/\bplease\b/g, ''], [/\bcalculate karo\b/g, 'calculate'],
    [/\bbolo\b/g, 'tell'], [/\bdikhao\b/g, 'show'],
];

function expandIndianScale(str) {
    return str
        .replace(/(\d+(?:\.\d+)?)\s*(lakh|lac|lacs|lakhs)\b/gi, (_, n) => String(parseFloat(n) * 100000))
        .replace(/(\d+(?:\.\d+)?)\s*crore[s]?\b/gi, (_, n) => String(parseFloat(n) * 10000000))
        .replace(/(\d+(?:\.\d+)?)\s*(hazar|hazaar|thousand)\b/gi, (_, n) => String(parseFloat(n) * 1000))
        .replace(/(\d+(?:\.\d+)?)\s*million[s]?\b/gi, (_, n) => String(parseFloat(n) * 1000000))
        .replace(/(\d+(?:\.\d+)?)\s*billion[s]?\b/gi, (_, n) => String(parseFloat(n) * 1000000000));
}

// Very small word-number map (helpful for spoken input)
const WORD_NUM = {
    zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
    eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13,
    fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
    nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60,
    seventy: 70, eighty: 80, ninety: 90, hundred: 100,
};
function replaceWordNumbers(str) {
    return str.replace(/\b([a-z]+)\b/g, w => WORD_NUM[w] !== undefined ? String(WORD_NUM[w]) : w);
}

function normalize(raw) {
    let q = ' ' + raw.toLowerCase().trim().replace(/₹/g, ' rupees ') + ' ';
    let prev;
    do { prev = q; q = q.replace(/(\d),(\d{3})(?!\d)/g, '$1$2'); } while (q !== prev);
    q = q.replace(/,/g, ' ');
    q = spellCorrect(q);
    q = expandSynonyms(q);
    q = expandIndianScale(q);
    HINGLISH_MAP.forEach(([pattern, repl]) => { q = q.replace(pattern, repl); });
    q = replaceWordNumbers(q);
    q = q.replace(/\s+/g, ' ').trim();
    return q;
}

function allNums(q) {
    const found = q.match(/-?\d+(?:\.\d+)?/g);
    return found ? found.map(Number) : [];
}

function fmtNum(n, decimals = 2) {
    if (typeof n !== 'number' || isNaN(n)) return n;
    if (Number.isInteger(n)) return n.toLocaleString('en-IN');
    return n.toLocaleString('en-IN', { maximumFractionDigits: decimals });
}
function inr(n) { return '₹' + fmtNum(n); }

function parseDateSmart(str) {
    if (!str) return null;
    str = str.trim();
    if (/\b(today|aaj|aj|ajj|now)\b/.test(str)) return new Date();
    if (/\byesterday\b/.test(str)) { const d = new Date(); d.setDate(d.getDate() - 1); return d; }
    if (/\btomorrow\b/.test(str)) { const d = new Date(); d.setDate(d.getDate() + 1); return d; }
    let d = new Date(str);
    if (!isNaN(d)) return d;
    let m = str.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
    if (m) {
        let day = parseInt(m[1]), mon = parseInt(m[2]) - 1, yr = parseInt(m[3]);
        if (yr < 100) yr += 2000;
        d = new Date(yr, mon, day);
        if (!isNaN(d)) return d;
    }
    return null;
}

function factorial(n) { let r = 1; for (let i = 2; i <= n; i++) r *= i; return r; }
function nCr(n, r) { if (r > n || r < 0) return NaN; return factorial(n) / (factorial(r) * factorial(n - r)); }
function nPr(n, r) { if (r > n || r < 0) return NaN; return factorial(n) / factorial(n - r); }
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a; }
function lcm(a, b) { return Math.abs(a * b) / gcd(a, b); }

function splitDateSegments(cleaned) {
    let seTak = cleaned.match(/^(.*?)\bse\b(.*?)\btak\b(.*)$/);
    if (seTak) return [seTak[1].trim(), seTak[2].trim()];
    let parts = cleaned.split(' and ');
    if (parts.length >= 2) return [parts[0].trim(), parts.slice(1).join(' and ').trim()];
    let toParts = cleaned.split(/\s+to\s+/);
    if (toParts.length >= 2) return [toParts[0].trim(), toParts.slice(1).join(' to ').trim()];
    return null;
}

function extractRate(q) {
    let m = q.match(/(\d+(?:\.\d+)?)\s*%|(\d+(?:\.\d+)?)\s*percent/);
    return m ? parseFloat(m[1] || m[2]) : null;
}
function extractYears(q) {
    let m = q.match(/(\d+(?:\.\d+)?)\s*(years?|yrs?)\b/);
    return m ? parseFloat(m[1]) : null;
}
function extractMonths(q) {
    let m = q.match(/(\d+(?:\.\d+)?)\s*months?\b/);
    return m ? parseFloat(m[1]) : null;
}
function pickPrincipal(nums, exclude = []) {
    const remaining = nums.filter(n => !exclude.some(e => Math.abs(e - n) < 1e-9));
    if (!remaining.length) return null;
    return Math.max(...remaining);
}

/* ---------------------------------------------------------------
   RESPONSE BUILDERS
   --------------------------------------------------------------- */
function R({ result, type, formula, calc, explain, related = [], suggestions = [], warnings = [], alternative = null }) {
    return { mode: 'result', result, type, formula, calc, explain, related, suggestions, warnings, alternative };
}
function Clarify(intentId, missing, hint) {
    return { mode: 'clarify', intentId, missing, hint };
}
function Chat(message, suggestions = []) {
    return { mode: 'chat', message, suggestions };
}

/* ---------------------------------------------------------------
   UNIT TABLES
   --------------------------------------------------------------- */
const UNIT_GROUPS = {
    length: { m: 1, meter: 1, metre: 1, meters: 1, metres: 1, km: 1000, kilometer: 1000, kilometers: 1000, cm: 0.01, mm: 0.001, mile: 1609.344, miles: 1609.344, yard: 0.9144, yards: 0.9144, yd: 0.9144, foot: 0.3048, feet: 0.3048, ft: 0.3048, inch: 0.0254, inches: 0.0254, in: 0.0254, nm: 1852, 'nautical mile': 1852 },
    weight: { kg: 1, kilogram: 1, kilograms: 1, g: 0.001, gram: 0.001, grams: 0.001, mg: 0.000001, lb: 0.453592, lbs: 0.453592, pound: 0.453592, pounds: 0.453592, oz: 0.0283495, ounce: 0.0283495, ounces: 0.0283495, ton: 1000, tonne: 1000, quintal: 100 },
    volume: { l: 1, liter: 1, liters: 1, litre: 1, litres: 1, ml: 0.001, gallon: 3.78541, gallons: 3.78541, gal: 3.78541, cup: 0.24, cups: 0.24, pint: 0.473176, quart: 0.946353, 'fl oz': 0.0295735 },
    area: { sqm: 1, 'sq m': 1, sqft: 0.092903, 'sq ft': 0.092903, sqkm: 1000000, 'sq km': 1000000, acre: 4046.86, acres: 4046.86, hectare: 10000, hectares: 10000, sqyard: 0.836127, 'sq yard': 0.836127, sqmile: 2589988.11 },
    speed: { 'km/h': 1, kmph: 1, kmh: 1, 'm/s': 3.6, mps: 3.6, mph: 1.60934, knot: 1.852, knots: 1.852 },
    energy: { joule: 1, joules: 1, j: 1, kj: 1000, calorie: 4.184, calories: 4.184, cal: 4.184, kcal: 4184, kwh: 3600000 },
    pressure: { pascal: 1, pascals: 1, pa: 1, kpa: 1000, bar: 100000, psi: 6894.76, atm: 101325 },
    data: { bit: 1, bits: 1, byte: 8, bytes: 8, kb: 8000, mb: 8000000, gb: 8000000000, tb: 8000000000000 },
    time: { second: 1, seconds: 1, sec: 1, s: 1, minute: 60, minutes: 60, min: 60, hour: 3600, hours: 3600, hr: 3600, day: 86400, days: 86400, week: 604800, weeks: 604800 },
    angle: { degree: 1, degrees: 1, deg: 1, radian: 57.2958, radians: 57.2958, rad: 57.2958 },
};
function findUnitGroup(unit) {
    unit = unit.toLowerCase().trim();
    for (const [group, table] of Object.entries(UNIT_GROUPS)) {
        if (Object.prototype.hasOwnProperty.call(table, unit)) return { group, factor: table[unit] };
    }
    return null;
}
function convertTemperature(value, from, to) {
    from = from.toLowerCase(); to = to.toLowerCase();
    let celsius;
    if (from.startsWith('c')) celsius = value;
    else if (from.startsWith('f')) celsius = (value - 32) * 5 / 9;
    else if (from.startsWith('k')) celsius = value - 273.15;
    else return null;
    let out;
    if (to.startsWith('c')) out = celsius;
    else if (to.startsWith('f')) out = celsius * 9 / 5 + 32;
    else if (to.startsWith('k')) out = celsius + 273.15;
    else return null;
    return out;
}

/* ---------------------------------------------------------------
   MODULES
   --------------------------------------------------------------- */
const MODULES = [];

/* ---- SMALL TALK ---- */
MODULES.push({
    id: 'smallTalk',
    name: 'Small Talk',
    score(q) {
        if (/^(hi+|hello+|helo|hlo|heya|sup|hey+|yo|hola|namaste|namaskar|ram\s?ram|sat\s?sri\s?akal)\b/.test(q)) return 0.99;
        if (/^good\s+(morning|afternoon|evening|night)\b/.test(q)) return 0.99;
        if (/^(thanks|thank\s+you|thankyou|dhanyavaad|shukriya|thnx|thnks|thanku|tysm|\bty\b)\b/.test(q)) return 0.99;
        if (/^(bye|goodbye|see\s+you|alvida|tata|byee|bb|gtg|cya)\b/.test(q)) return 0.99;
        if (/^(who\s+are\s+you|what\s+can\s+you\s+do|what\s+do\s+you\s+do|help|about\s+you)\b/.test(q)) return 0.99;
        if (/^how\s+are\s+you\b/.test(q)) return 0.99;
        // ===== NEW FEATURE START ===== (broader small-talk coverage so more inputs get a
        // natural reply instead of falling through to the generic "unknown" fallback)
        if (/\b(tell\s+me\s+a\s+joke|joke\s+sunao|say\s+a\s+joke|make\s+me\s+laugh|\bjoke\b)\b/.test(q)) return 0.97;
        if (/\b(who\s+(made|created|built)\s+you|who\s+is\s+your\s+(creator|developer|maker))\b/.test(q)) return 0.97;
        if (/\b(is\s+this\s+free|are\s+you\s+free|does\s+this\s+cost\s+money|any\s+charge)\b/.test(q)) return 0.95;
        if (/^(you\s*'?(re|are)\s+(so\s+)?(smart|great|awesome|amazing|good|the\s+best|cool|brilliant)|good\s+job|well\s+done|nice\s+work|great\s+job)\b/.test(q)) return 0.95;
        if (/^(you\s*'?(re|are)\s+(so\s+)?(bad|useless|dumb|stupid|bakwas|bekar|worst|terrible))\b/.test(q)) return 0.95;
        // Reaction-only words: anchored with $ so "ok calculate 15% of 2500" still
        // reaches the percentage module instead of being swallowed here.
        if (/^(wow|cool|nice|awesome|great|amazing|superb|zabardast|badhiya)[.!\s]*$/.test(q)) return 0.9;
        if (/^(ok|okay|alright|fine|acha|theek\s*hai|thik\s*hai)[.!\s]*$/.test(q)) return 0.85;
        // ===== NEW FEATURE END =====
        return 0;
    },
    run(q) {
        if (/^good\s+morning/.test(q)) return Chat("Good morning! Ready to crunch some numbers?", ["15% of 2500", "emi for 5 lakh at 8.5% for 5 years"]);
        if (/^good\s+afternoon/.test(q)) return Chat("Good afternoon! What shall I calculate for you?", ["sip of 5000 at 12% for 10 years", "18% gst on 1500"]);
        if (/^good\s+evening/.test(q)) return Chat("Good evening! I'm here to help with any calculation.", ["bmi 70kg 170cm", "100 usd to inr"]);
        if (/^good\s+night/.test(q)) return Chat("Good night! Sweet dreams — I'll be here whenever you need me.", []);
        if (/^(hi+|hello+|helo|hlo|heya|sup|hey+|yo|hola)\b/.test(q)) return Chat(pick(GREETING_REPLIES), ["emi for 10 lakh at 8% for 15 years", "compound interest on 50000 at 7% for 5 years", "bmi 65kg 168cm"]);
        if (/namaste|namaskar|ram\s?ram|sat\s?sri\s?akal/.test(q)) return Chat("Namaste! 🙏 Aap kya calculate karna chahenge?", ["15% of 2500", "sip of 5000 per month at 12% for 10 years"]);
        if (/^(thanks|thank\s+you|thankyou|dhanyavaad|shukriya|thnx|thnks|thanku|tysm|ty)\b/.test(q)) return Chat(pick(THANKS_REPLIES), []);
        if (/^(bye|goodbye|see\s+you|alvida|tata|byee|bb|gtg|cya)\b/.test(q)) return Chat(pick(BYE_REPLIES), []);
        if (/^how\s+are\s+you/.test(q)) return Chat("I'm running smooth at 100 GHz of intent-detection. How can I help you calculate today?", []);
        if (/^(who\s+are\s+you|about\s+you)/.test(q)) return Chat("I'm smartcalcAi — an intelligent AI calculator that understands natural language. I can handle finance (EMI, SIP, FD, GST), health (BMI, calories), math, statistics, unit conversion, currency, and more. Just ask.", ["What can you do", "emi for 5 lakh at 8% for 5 years"]);
        if (/^(what\s+can\s+you\s+do|what\s+do\s+you\s+do|help)/.test(q)) {
            return Chat("Here's a taste of what I can do:\n• Finance — EMI, SIP, FD, RD, GST, discount, compound/simple interest, salary, inflation\n• Health — BMI, calories/BMR, water intake, ideal weight\n• Math — statistics, permutation/combination, probability, trig, logs, factorial, quadratic, LCM/GCD\n• Business — profit, loss, margin, markup, CAGR, ROI, depreciation\n• Everyday — unit & currency conversion, dates, age, percentage\nSpeak English, Hindi or Hinglish — I understand it all!",
                ["emi for 5 lakh at 8.5% for 5 years", "cagr from 10000 to 25000 in 5 years", "profit if cp 500 sp 650", "lcm of 12 and 18"]);
        }
        // ===== NEW FEATURE START =====
        if (/\b(tell\s+me\s+a\s+joke|joke\s+sunao|say\s+a\s+joke|make\s+me\s+laugh|\bjoke\b)\b/.test(q)) return Chat(pick(JOKES), ["15% of 2500", "tell me a joke"]);
        if (/\b(who\s+(made|created|built)\s+you|who\s+is\s+your\s+(creator|developer|maker))\b/.test(q)) return Chat("I'm smartcalcAi — built to turn plain-language questions into instant calculations, no formulas needed on your end. 🛠️", ["what can you do"]);
        if (/\b(is\s+this\s+free|are\s+you\s+free|does\s+this\s+cost\s+money|any\s+charge)\b/.test(q)) return Chat("Yep, completely free to use — no sign-up, no charges. 🎉", []);
        if (/^(you\s*'?(re|are)\s+(so\s+)?(smart|great|awesome|amazing|good|the\s+best|cool|brilliant)|good\s+job|well\s+done|nice\s+work|great\s+job)\b/.test(q)) return Chat(pick(COMPLIMENT_REPLIES), []);
        if (/^(you\s*'?(re|are)\s+(so\s+)?(bad|useless|dumb|stupid|bakwas|bekar|worst|terrible))\b/.test(q)) return Chat(pick(INSULT_REPLIES), ["what can you do"]);
        if (/^(wow|cool|nice|awesome|great|amazing|superb|zabardast|badhiya)[.!\s]*$/.test(q)) return Chat(pick(REACTION_REPLIES), []);
        if (/^(ok|okay|alright|fine|acha|theek\s*hai|thik\s*hai)[.!\s]*$/.test(q)) return Chat(pick(ACK_REPLIES), []);
        // ===== NEW FEATURE END =====
        return Chat("Hi! How can I help?", []);
    }
});

/* ---- Date Difference ---- */
MODULES.push({
    id: 'dateDiff',
    name: 'Date Difference',
    score(q) {
        if (/years between|months between|days between|date difference|date diff/.test(q)) return 0.95;
        if (/\bse\b.*\btak\b/.test(q)) return 0.9;
        return 0;
    },
    run(q) {
        let unitLabel = /\byears\b/.test(q) ? 'years' : /\bmonths\b/.test(q) ? 'months' : /\bdays\b/.test(q) ? 'days' : 'full';
        let cleaned = q.replace(/(years|months|days) between|date difference|date diff/, '')
            .replace(/\b(years|months|days|of|calculate|tell|how much|how many)\b/g, ' ')
            .replace(/\s+/g, ' ').trim();
        let segments = splitDateSegments(cleaned);
        if (!segments) return Clarify('dateDiff', ['start date', 'end date'], "e.g. 'days between 1 Jan 2020 and today' or '2005 se aaj tak'");
        let d1 = parseDateSmart(segments[0]);
        let d2 = parseDateSmart(segments[1]);
        if (!d1 || !d2) return Clarify('dateDiff', ['a valid start date', 'a valid end date'], "Try formats like '5 Jan 2005' or '05/01/2005'.");
        let [early, late] = d1 <= d2 ? [d1, d2] : [d2, d1];
        let years = late.getFullYear() - early.getFullYear();
        let months = late.getMonth() - early.getMonth();
        let days = late.getDate() - early.getDate();
        if (days < 0) { months--; days += new Date(late.getFullYear(), late.getMonth(), 0).getDate(); }
        if (months < 0) { years--; months += 12; }
        let totalDays = Math.round(Math.abs(late - early) / 86400000);

        let resultText, calc;
        if (unitLabel === 'years') { resultText = `${years} Years`; calc = `${late.getFullYear()} − ${early.getFullYear()} (adjusted for month/day)`; }
        else if (unitLabel === 'months') { resultText = `${years * 12 + months} Months`; calc = `${years}y ${months}m converted to months`; }
        else if (unitLabel === 'days') { resultText = `${totalDays} Days`; calc = `${late.toDateString()} − ${early.toDateString()}`; }
        else { resultText = `${years}y ${months}m ${days}d`; calc = `Full breakdown between ${early.toDateString()} and ${late.toDateString()}`; }

        return R({
            result: resultText, type: 'Date Difference', formula: 'End Date − Start Date',
            calc, explain: `The gap between ${early.toDateString()} and ${late.toDateString()} is ${years} years, ${months} months and ${days} days (${totalDays} total days).`,
            related: [`Total days: ${fmtNum(totalDays)}`, `Total weeks: ~${fmtNum(Math.round(totalDays / 7))}`],
            suggestions: ['age from ' + segments[0].trim(), 'days between ' + segments[0].trim() + ' and today'],
        });
    }
});

/* ---- Age ---- */
MODULES.push({
    id: 'age',
    name: 'Age Calculator',
    score(q) { return /\bage from\b|\bage between\b|\bmy age\b|\bage on\b|\bage if born\b/.test(q) ? 0.95 : 0; },
    run(q) {
        let datePart = q.replace(/age from|age between|my age if born on|age on|age if born on/g, '').split(' and ')[0].trim();
        let birth = parseDateSmart(datePart);
        if (!birth) return Clarify('age', ['date of birth'], "e.g. 'age from 15 Aug 2000'");
        let today = new Date();
        let years = today.getFullYear() - birth.getFullYear();
        let months = today.getMonth() - birth.getMonth();
        let days = today.getDate() - birth.getDate();
        if (days < 0) { months--; days += new Date(today.getFullYear(), today.getMonth(), 0).getDate(); }
        if (months < 0) { years--; months += 12; }
        let totalDays = Math.round(Math.abs(today - birth) / 86400000);
        return R({
            result: `${years} Years, ${months} Months, ${days} Days`, type: 'Age Calculator',
            formula: 'Today − Date of Birth', calc: `Born ${birth.toDateString()} → Today ${today.toDateString()}`,
            explain: `As of today, the exact age is ${years} years, ${months} months and ${days} days (${fmtNum(totalDays)} total days lived).`,
            related: [`Total days lived: ${fmtNum(totalDays)}`, `Total weeks: ~${fmtNum(Math.round(totalDays / 7))}`],
            suggestions: ['days between ' + datePart + ' and today'],
        });
    }
});

/* ---- Date Math ---- */
MODULES.push({
    id: 'dateMath',
    name: 'Date Addition/Subtraction',
    score(q) { return /\d+\s*(days?|months?|years?)\s*(after|before|from)\b/.test(q) ? 0.92 : 0; },
    run(q) {
        let m1 = q.match(/^(.*?)\bse\b\s*(\d+)\s*(days?|months?|years?)\s*(after|before)\b(.*)$/);
        let m2 = !m1 && q.match(/(\d+)\s*(days?|months?|years?)\s*(after|before|from)\s*(.*)/);
        let amount, unit, direction, baseStr;
        if (m1) { amount = parseInt(m1[2]); unit = m1[3]; direction = m1[4] === 'before' ? -1 : 1; baseStr = m1[1]; }
        else if (m2) { amount = parseInt(m2[1]); unit = m2[2]; direction = m2[3] === 'before' ? -1 : 1; baseStr = m2[4]; }
        else return 0;

        unit = unit.startsWith('day') ? 'days' : unit.startsWith('month') ? 'months' : 'years';
        baseStr = baseStr.replace(/\b(which|what|date|will|it|be|is|calculate|tell)\b/g, ' ').replace(/\s+/g, ' ').trim();
        let base = parseDateSmart(baseStr) || new Date();
        let result = new Date(base);
        if (unit === 'days') result.setDate(result.getDate() + amount * direction);
        else if (unit === 'months') result.setMonth(result.getMonth() + amount * direction);
        else result.setFullYear(result.getFullYear() + amount * direction);

        let dirLabel = direction === 1 ? 'after' : 'before';
        return R({
            result: result.toDateString(), type: `Date ${direction === 1 ? 'Addition' : 'Subtraction'}`,
            formula: `Base Date ${direction === 1 ? '+' : '−'} ${amount} ${unit}`,
            calc: `${base.toDateString()} ${direction === 1 ? '+' : '−'} ${amount} ${unit}`,
            explain: `${amount} ${unit} ${dirLabel} ${base.toDateString()} falls on ${result.toDateString()}.`,
            related: [`Day of week: ${result.toLocaleDateString('en-US', { weekday: 'long' })}`],
            suggestions: [`days between ${base.toDateString()} and ${result.toDateString()}`],
        });
    }
});

/* ---- Percentage ---- */
MODULES.push({
    id: 'percentage',
    name: 'Percentage',
    score(q) {
        if (/what percent|kitna percent|percent is/.test(q)) return 0.9;
        if (/increase .* by \d+(\.\d+)?\s*%|decrease .* by \d+(\.\d+)?\s*%/.test(q)) return 0.9;
        if (/%/.test(q) || /percent/.test(q)) return 0.85;
        return 0;
    },
    run(q, nums) {
        let incDec = q.match(/(increase|decrease)\s*(\d+(?:\.\d+)?)\s*by\s*(\d+(?:\.\d+)?)\s*%/);
        if (incDec) {
            let dir = incDec[1], base = parseFloat(incDec[2]), pct = parseFloat(incDec[3]);
            let res = dir === 'increase' ? base * (1 + pct / 100) : base * (1 - pct / 100);
            return R({
                result: fmtNum(res), type: `Percentage ${dir === 'increase' ? 'Increase' : 'Decrease'}`,
                formula: dir === 'increase' ? 'Base × (1 + %/100)' : 'Base × (1 − %/100)',
                calc: `${base} × (1 ${dir === 'increase' ? '+' : '−'} ${pct}/100)`,
                explain: `${dir === 'increase' ? 'Increasing' : 'Decreasing'} ${base} by ${pct}% gives ${fmtNum(res)}.`,
                suggestions: [`${dir === 'increase' ? 'decrease' : 'increase'} ${base} by ${pct}%`],
            });
        }
        let whatPercent = q.match(/what percent is\s*(\d+(?:\.\d+)?)\s*of\s*(\d+(?:\.\d+)?)/) || q.match(/(\d+(?:\.\d+)?)\s*of\s*(\d+(?:\.\d+)?)\s*is what percent/);
        if (whatPercent) {
            let a = parseFloat(whatPercent[1]), b = parseFloat(whatPercent[2]);
            let res = (a / b) * 100;
            return R({
                result: fmtNum(res) + '%', type: 'What Percent', formula: '(Part / Whole) × 100',
                calc: `(${a} / ${b}) × 100`, explain: `${a} is ${fmtNum(res)}% of ${b}.`,
            });
        }
        let pct = extractRate(q);
        if (pct !== null) {
            let base = pickPrincipal(nums, [pct]);
            if (base === null) return Clarify('percentage', ['the base amount'], "e.g. '15% of 2500'");
            let res = (base * pct) / 100;
            return R({
                result: fmtNum(res), type: 'Percentage', formula: 'Amount × Percent / 100',
                calc: `${base} × ${pct} / 100`, explain: `${pct}% of ${fmtNum(base)} is ${fmtNum(res)}.`,
                suggestions: [`what percent is ${fmtNum(res)} of ${base}`, `increase ${base} by ${pct}%`],
            });
        }
        return 0;
    }
});

/* ---- GST ---- */
MODULES.push({
    id: 'gst',
    name: 'GST',
    score(q) { return /\bgst\b/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let base = pickPrincipal(nums, rate !== null ? [rate] : []);
        if (rate === null || base === null) return Clarify('gst', ['the GST rate (%)', 'the amount'], "e.g. '18% GST on 1500' or 'remove 18% GST from 1180'");
        let isReverse = /remove|reverse|exclude|excluding|already includ/.test(q);
        if (isReverse) {
            let net = base / (1 + rate / 100);
            let tax = base - net;
            return R({
                result: inr(net), type: 'Reverse GST (Exclusive Price)', formula: 'Base = Total / (1 + Rate/100)',
                calc: `${base} / (1 + ${rate}/100)`, explain: `If ${inr(base)} already includes ${rate}% GST, the base price is ${inr(net)} and the GST portion is ${inr(tax)}.`,
                related: [`GST amount: ${inr(tax)}`],
                suggestions: [`${rate}% gst on ${fmtNum(net)}`, `${rate}% discount on ${base}`],
            });
        }
        let tax = (base * rate) / 100;
        let total = base + tax;
        return R({
            result: inr(total), type: 'GST (Inclusive Price)', formula: 'Total = Base + (Base × Rate/100)',
            calc: `${base} + (${base} × ${rate}/100)`, explain: `${rate}% GST on ${inr(base)} adds ${inr(tax)} tax, making the total ${inr(total)}.`,
            related: [`GST amount: ${inr(tax)}`],
            suggestions: [`remove ${rate}% gst from ${fmtNum(total)}`, `${rate}% discount on ${base}`],
        });
    }
});

/* ---- Discount ---- */
MODULES.push({
    id: 'discount',
    name: 'Discount',
    score(q) { return /discount/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let base = pickPrincipal(nums, rate !== null ? [rate] : []);
        if (rate === null || base === null) return Clarify('discount', ['the discount %', 'the original price'], "e.g. '20% discount on 500'");
        let saved = (base * rate) / 100;
        let final = base - saved;
        return R({
            result: inr(final), type: 'Discount', formula: 'Final = Price × (1 − Rate/100)',
            calc: `${base} − (${base} × ${rate}/100)`, explain: `A ${rate}% discount on ${inr(base)} saves ${inr(saved)}, so the final price is ${inr(final)}.`,
            related: [`You save: ${inr(saved)}`],
            suggestions: [`18% gst on ${fmtNum(final)}`],
        });
    }
});

/* ---- EMI ---- */
MODULES.push({
    id: 'emi',
    name: 'Loan EMI',
    score(q) { return /\bemi\b|home loan|car loan|personal loan|\bloan\b/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let months = extractMonths(q) || (years ? years * 12 : null);
        let principal = pickPrincipal(nums, [rate, years, extractMonths(q)].filter(v => v !== null && v !== undefined));
        let missing = [];
        if (principal === null) missing.push('the loan amount');
        if (rate === null) missing.push('the interest rate (%)');
        if (!months) missing.push('the loan tenure (years or months)');
        if (missing.length) return Clarify('emi', missing, "e.g. 'EMI for 5 lakh at 8.5% for 5 years'");
        let r = rate / 12 / 100;
        let n = months;
        let emi = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        let totalPayment = emi * n;
        let totalInterest = totalPayment - principal;
        return R({
            result: inr(emi), type: 'Loan EMI', formula: 'P × r × (1+r)ⁿ / ((1+r)ⁿ − 1)',
            calc: `P=${fmtNum(principal)}, r=${(r * 100).toFixed(4)}%/mo, n=${n} months`,
            explain: `A loan of ${inr(principal)} at ${rate}% annual interest for ${n} months has a monthly EMI of ${inr(emi)}.`,
            related: [`Total interest: ${inr(totalInterest)}`, `Total payment: ${inr(totalPayment)}`],
            suggestions: [`emi for ${principal} at ${rate - 1}% for ${months / 12} years`, `emi for ${principal} at ${rate}% for ${(months / 12) + 5} years`],
            warnings: ['Reducing-balance estimate — banks may add processing fees or insurance.'],
        });
    }
});

/* ---- Compound Interest ---- */
MODULES.push({
    id: 'compoundInterest',
    name: 'Compound Interest',
    score(q) { return /compound interest/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let principal = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (principal === null || rate === null || years === null) return Clarify('compoundInterest', ['principal amount', 'rate (%)', 'time (years)'], "e.g. 'compound interest on 10000 at 5% for 3 years'");
        let amount = principal * Math.pow(1 + rate / 100, years);
        let ci = amount - principal;
        return R({
            result: inr(amount), type: 'Compound Interest', formula: 'A = P(1 + r/100)ᵗ',
            calc: `${principal} × (1 + ${rate}/100)^${years}`,
            explain: `₹${fmtNum(principal)} invested at ${rate}% compounded annually for ${years} years grows to ${inr(amount)}, earning ${inr(ci)} interest.`,
            related: [`Interest earned: ${inr(ci)}`],
            suggestions: [`simple interest on ${principal} at ${rate}% for ${years} years`],
        });
    }
});

/* ---- Simple Interest ---- */
MODULES.push({
    id: 'simpleInterest',
    name: 'Simple Interest',
    score(q) { return /simple interest/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let principal = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (principal === null || rate === null || years === null) return Clarify('simpleInterest', ['principal amount', 'rate (%)', 'time (years)'], "e.g. 'simple interest on 10000 at 5% for 3 years'");
        let si = (principal * rate * years) / 100;
        let amount = principal + si;
        return R({
            result: inr(si), type: 'Simple Interest', formula: 'SI = P × R × T / 100',
            calc: `${principal} × ${rate} × ${years} / 100`,
            explain: `₹${fmtNum(principal)} at ${rate}% simple interest for ${years} years earns ${inr(si)}, totalling ${inr(amount)}.`,
            related: [`Maturity amount: ${inr(amount)}`],
            suggestions: [`compound interest on ${principal} at ${rate}% for ${years} years`],
        });
    }
});

/* ---- SIP ---- */
MODULES.push({
    id: 'sip',
    name: 'SIP',
    score(q) { return /\bsip\b|mutual fund/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let monthly = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (monthly === null || rate === null || years === null) return Clarify('sip', ['monthly investment amount', 'expected annual return (%)', 'duration (years)'], "e.g. 'SIP of 5000 per month at 12% for 10 years'");
        let i = rate / 12 / 100;
        let n = years * 12;
        let fv = monthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
        let invested = monthly * n;
        let gain = fv - invested;
        return R({
            result: inr(fv), type: 'SIP Future Value', formula: 'FV = P × [((1+i)ⁿ−1)/i] × (1+i)',
            calc: `P=${monthly}/mo, i=${(i * 100).toFixed(4)}%, n=${n} months`,
            explain: `Investing ${inr(monthly)} monthly at ${rate}% expected annual return for ${years} years grows to ${inr(fv)}.`,
            related: [`Total invested: ${inr(invested)}`, `Wealth gained: ${inr(gain)}`],
            suggestions: [`fd of ${invested} at ${rate}% for ${years} years`],
            warnings: ['Mutual fund returns are market-linked, not guaranteed.'],
        });
    }
});

/* ---- FD ---- */
MODULES.push({
    id: 'fd',
    name: 'Fixed Deposit',
    score(q) { return /\bfd\b|fixed deposit/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let principal = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (principal === null || rate === null || years === null) return Clarify('fd', ['deposit amount', 'interest rate (%)', 'tenure (years)'], "e.g. 'FD of 100000 at 6.5% for 3 years'");
        let n = 4;
        let amount = principal * Math.pow(1 + rate / (n * 100), n * years);
        let interest = amount - principal;
        return R({
            result: inr(amount), type: 'Fixed Deposit Maturity', formula: 'A = P(1 + r/4n)^(4t) — quarterly compounding',
            calc: `${principal} × (1 + ${rate}/400)^${n * years}`,
            explain: `₹${fmtNum(principal)} in an FD at ${rate}% (quarterly compounding) for ${years} years matures to ${inr(amount)}.`,
            related: [`Interest earned: ${inr(interest)}`],
            suggestions: [`rd of ${Math.round(principal / (years * 12))} per month at ${rate}% for ${years} years`],
            warnings: ['Actual bank compounding frequency may vary.'],
        });
    }
});

/* ---- RD ---- */
MODULES.push({
    id: 'rd',
    name: 'Recurring Deposit',
    score(q) { return /\brd\b|recurring deposit/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let monthly = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (monthly === null || rate === null || years === null) return Clarify('rd', ['monthly deposit amount', 'interest rate (%)', 'tenure (years)'], "e.g. 'RD of 2000 per month at 6% for 2 years'");
        let n = years * 12;
        let invested = monthly * n;
        let maturity = invested + (monthly * n * (n + 1) / 2) * (rate / (12 * 100));
        let interest = maturity - invested;
        return R({
            result: inr(maturity), type: 'Recurring Deposit Maturity', formula: 'A ≈ Invested + P×n(n+1)/2 × r/(12×100)',
            calc: `P=${monthly}/mo, n=${n} months, r=${rate}%`,
            explain: `Depositing ${inr(monthly)} monthly at ${rate}% for ${years} years grows to approximately ${inr(maturity)}.`,
            related: [`Total invested: ${inr(invested)}`, `Interest earned: ${inr(interest)}`],
            warnings: ['Approximation — actual bank RD formulas vary slightly.'],
        });
    }
});

/* ---- BMI ---- */
MODULES.push({
    id: 'bmi',
    name: 'BMI',
    score(q) { return /\bbmi\b/.test(q) ? 0.95 : 0; },
    run(q) {
        let kg = q.match(/(\d+(?:\.\d+)?)\s*kg/);
        let cm = q.match(/(\d+(?:\.\d+)?)\s*cm/);
        let m = q.match(/(\d+(?:\.\d+)?)\s*m\b/);
        if (!kg || (!cm && !m)) return Clarify('bmi', ['weight in kg', 'height in cm'], "e.g. 'BMI 70kg 170cm'");
        let w = parseFloat(kg[1]);
        let h = cm ? parseFloat(cm[1]) / 100 : parseFloat(m[1]);
        let bmi = w / (h * h);
        let status = bmi < 18.5 ? 'Underweight' : bmi < 25 ? 'Normal weight' : bmi < 30 ? 'Overweight' : 'Obese';
        return R({
            result: bmi.toFixed(1), type: 'Body Mass Index', formula: 'Weight (kg) / Height² (m²)',
            calc: `${w} / (${h.toFixed(2)} × ${h.toFixed(2)})`,
            explain: `A BMI of ${bmi.toFixed(1)} falls in the "${status}" category (WHO standard ranges).`,
            related: [`Category: ${status}`],
            suggestions: [`water intake for ${w}kg`, `ideal weight for ${Math.round(h * 100)}cm`],
            warnings: ['BMI does not account for muscle mass, age or body composition.'],
        });
    }
});

/* ---- Ideal Weight (Devine formula) ---- */
MODULES.push({
    id: 'idealWeight',
    name: 'Ideal Weight',
    score(q) { return /ideal weight/.test(q) ? 0.95 : 0; },
    run(q) {
        let cm = q.match(/(\d+(?:\.\d+)?)\s*cm/);
        if (!cm) return Clarify('idealWeight', ['height in cm'], "e.g. 'ideal weight for 170cm male'");
        let h = parseFloat(cm[1]);
        let inches = h / 2.54;
        let over5ft = Math.max(0, inches - 60);
        let male = /\bmale\b|\bman\b/.test(q);
        let base = male ? 50 : 45.5;
        let ideal = base + 2.3 * over5ft;
        return R({
            result: `${ideal.toFixed(1)} kg`, type: 'Ideal Weight (Devine)', formula: `${base} + 2.3 × (height_in − 60)`,
            calc: `Height = ${inches.toFixed(1)} in, over 5ft = ${over5ft.toFixed(1)} in`,
            explain: `The Devine formula suggests an ideal weight of about ${ideal.toFixed(1)} kg for a ${male ? 'male' : 'female'} of height ${h} cm.`,
            warnings: ['Formulas vary — this is a general clinical estimate, not medical advice.'],
        });
    }
});

/* ---- Water Intake ---- */
MODULES.push({
    id: 'water',
    name: 'Water Intake',
    score(q) { return /water intake|water requirement/.test(q) ? 0.9 : 0; },
    run(q) {
        let kg = q.match(/(\d+(?:\.\d+)?)\s*kg/);
        if (!kg) return Clarify('water', ['body weight in kg'], "e.g. 'water intake for 70kg'");
        let w = parseFloat(kg[1]);
        let liters = (w * 35) / 1000;
        return R({
            result: `${liters.toFixed(1)} Litres/day`, type: 'Daily Water Intake', formula: 'Weight (kg) × 35 ml',
            calc: `${w} × 35 ml`, explain: `A general guideline is ~35ml water per kg body weight, so ${w}kg suggests about ${liters.toFixed(1)} litres per day.`,
            warnings: ['Needs vary with activity, climate and health.'],
        });
    }
});

/* ---- Calories (Mifflin-St Jeor) ---- */
MODULES.push({
    id: 'calories',
    name: 'Calories / BMR',
    score(q) { return /\bcalories\b|\bbmr\b|\btdee\b|maintenance calories/.test(q) ? 0.85 : 0; },
    run(q) {
        let kg = q.match(/(\d+(?:\.\d+)?)\s*kg/);
        let cm = q.match(/(\d+(?:\.\d+)?)\s*cm/);
        let age = q.match(/(\d+)\s*(?:years?|yrs?) old|age\s*(\d+)/);
        let male = /\bmale\b|\bman\b/.test(q);
        let female = /\bfemale\b|\bwoman\b/.test(q);
        let missing = [];
        if (!kg) missing.push('weight in kg');
        if (!cm) missing.push('height in cm');
        if (!age) missing.push('age');
        if (!male && !female) missing.push('gender (male/female)');
        if (missing.length) return Clarify('calories', missing, "e.g. 'calories for 70kg male 25 years 170cm'");
        let w = parseFloat(kg[1]), h = parseFloat(cm[1]), a = parseInt(age[1] || age[2]);
        let bmr = male ? (10 * w + 6.25 * h - 5 * a + 5) : (10 * w + 6.25 * h - 5 * a - 161);
        let activityMap = [
            [/very active|athlete/, 1.9], [/active/, 1.725], [/moderate/, 1.55], [/light/, 1.375], [/sedentary/, 1.2],
        ];
        let mult = 1.2, activityLabel = 'sedentary';
        for (const [re, mm] of activityMap) { if (re.test(q)) { mult = mm; activityLabel = re.source.replace(/[|\\]/g, ' or '); break; } }
        let tdee = bmr * mult;
        return R({
            result: `${Math.round(tdee)} kcal/day`, type: 'Daily Calorie Requirement (TDEE)', formula: 'Mifflin-St Jeor BMR × Activity Factor',
            calc: `BMR=${Math.round(bmr)} kcal × ${mult}`,
            explain: `BMR is ~${Math.round(bmr)} kcal/day; at "${activityLabel}" activity level, estimated maintenance is ${Math.round(tdee)} kcal/day.`,
            related: [`BMR: ${Math.round(bmr)} kcal/day`],
            warnings: ['Estimate only — actual needs vary by individual.'],
        });
    }
});

/* ---- Profit / Loss ---- */
MODULES.push({
    id: 'profitLoss',
    name: 'Profit / Loss',
    score(q) { return /profit|loss|\bcp\b|\bsp\b|cost price|selling price/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let cp = null, sp = null;
        let m1 = q.match(/cp\s*(\d+(?:\.\d+)?)/); if (m1) cp = parseFloat(m1[1]);
        let m2 = q.match(/sp\s*(\d+(?:\.\d+)?)/); if (m2) sp = parseFloat(m2[1]);
        if (cp === null || sp === null) {
            if (nums.length >= 2) { cp = nums[0]; sp = nums[1]; }
            else return Clarify('profitLoss', ['cost price (CP)', 'selling price (SP)'], "e.g. 'profit if cp 500 sp 650'");
        }
        let diff = sp - cp;
        let type = diff >= 0 ? 'Profit' : 'Loss';
        let pct = Math.abs(diff) / cp * 100;
        return R({
            result: `${type}: ${inr(Math.abs(diff))} (${fmtNum(pct)}%)`, type: `${type} Calculation`,
            formula: `${type} = |SP − CP|,  ${type}% = ${type}/CP × 100`,
            calc: `SP=${sp}, CP=${cp}`,
            explain: `With cost price ${inr(cp)} and selling price ${inr(sp)}, there is a ${type.toLowerCase()} of ${inr(Math.abs(diff))} (${fmtNum(pct)}%).`,
            suggestions: [`margin if cp ${cp} sp ${sp}`, `markup if cp ${cp} sp ${sp}`],
        });
    }
});

/* ---- Margin ---- */
MODULES.push({
    id: 'margin',
    name: 'Margin',
    score(q) { return /\bmargin\b/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let cp = null, sp = null;
        let m1 = q.match(/cp\s*(\d+(?:\.\d+)?)/); if (m1) cp = parseFloat(m1[1]);
        let m2 = q.match(/sp\s*(\d+(?:\.\d+)?)/); if (m2) sp = parseFloat(m2[1]);
        if ((cp === null || sp === null) && nums.length >= 2) { cp = nums[0]; sp = nums[1]; }
        if (cp === null || sp === null) return Clarify('margin', ['cost', 'selling price'], "e.g. 'margin if cp 500 sp 650'");
        let margin = (sp - cp) / sp * 100;
        return R({
            result: `${fmtNum(margin)}%`, type: 'Profit Margin', formula: '(SP − CP) / SP × 100',
            calc: `(${sp} − ${cp}) / ${sp} × 100`,
            explain: `A selling price of ${inr(sp)} on cost ${inr(cp)} gives a margin of ${fmtNum(margin)}%.`,
            related: [`Markup: ${fmtNum((sp - cp) / cp * 100)}%`],
        });
    }
});

/* ---- Markup ---- */
MODULES.push({
    id: 'markup',
    name: 'Markup',
    score(q) { return /\bmarkup\b/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let cp = null, sp = null;
        let m1 = q.match(/cp\s*(\d+(?:\.\d+)?)/); if (m1) cp = parseFloat(m1[1]);
        let m2 = q.match(/sp\s*(\d+(?:\.\d+)?)/); if (m2) sp = parseFloat(m2[1]);
        if ((cp === null || sp === null) && nums.length >= 2) { cp = nums[0]; sp = nums[1]; }
        if (cp === null || sp === null) return Clarify('markup', ['cost', 'selling price'], "e.g. 'markup if cp 500 sp 650'");
        let markup = (sp - cp) / cp * 100;
        return R({
            result: `${fmtNum(markup)}%`, type: 'Markup', formula: '(SP − CP) / CP × 100',
            calc: `(${sp} − ${cp}) / ${cp} × 100`,
            explain: `${inr(cp)} marked up to ${inr(sp)} equals a ${fmtNum(markup)}% markup.`,
        });
    }
});

/* ---- CAGR ---- */
MODULES.push({
    id: 'cagr',
    name: 'CAGR',
    score(q) { return /\bcagr\b|compound annual growth/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        let years = extractYears(q);
        if (!years || nums.length < 2) return Clarify('cagr', ['initial value', 'final value', 'number of years'], "e.g. 'cagr from 10000 to 25000 in 5 years'");
        let excluded = [years];
        let filtered = nums.filter(n => !excluded.includes(n));
        if (filtered.length < 2) return Clarify('cagr', ['initial value', 'final value'], "e.g. 'cagr from 10000 to 25000 in 5 years'");
        let initial = filtered[0], final = filtered[1];
        let cagr = (Math.pow(final / initial, 1 / years) - 1) * 100;
        return R({
            result: `${fmtNum(cagr)}%`, type: 'CAGR', formula: '((Final / Initial)^(1/years) − 1) × 100',
            calc: `((${final} / ${initial})^(1/${years}) − 1) × 100`,
            explain: `An investment growing from ${inr(initial)} to ${inr(final)} over ${years} years represents a CAGR of ${fmtNum(cagr)}%.`,
        });
    }
});

/* ---- ROI ---- */
MODULES.push({
    id: 'roi',
    name: 'ROI',
    score(q) { return /\broi\b|return on investment/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('roi', ['investment', 'final value'], "e.g. 'roi from 10000 to 15000'");
        let [inv, fin] = nums;
        let roi = (fin - inv) / inv * 100;
        return R({
            result: `${fmtNum(roi)}%`, type: 'ROI', formula: '(Final − Investment) / Investment × 100',
            calc: `(${fin} − ${inv}) / ${inv} × 100`,
            explain: `Investing ${inr(inv)} and receiving ${inr(fin)} yields an ROI of ${fmtNum(roi)}%.`,
        });
    }
});

/* ---- Depreciation ---- */
MODULES.push({
    id: 'depreciation',
    name: 'Depreciation',
    score(q) { return /depreciation/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let cost = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (cost === null || rate === null || years === null) return Clarify('depreciation', ['original cost', 'depreciation rate (%)', 'years'], "e.g. 'depreciation of 100000 at 10% for 5 years'");
        let value = cost * Math.pow(1 - rate / 100, years);
        let dep = cost - value;
        return R({
            result: inr(value), type: 'Depreciation (Reducing Balance)', formula: 'V = C × (1 − r/100)ᵗ',
            calc: `${cost} × (1 − ${rate}/100)^${years}`,
            explain: `An asset worth ${inr(cost)} depreciating at ${rate}%/year for ${years} years is worth ${inr(value)} today (total depreciation ${inr(dep)}).`,
        });
    }
});

/* ---- CGPA ---- */
MODULES.push({
    id: 'cgpa',
    name: 'CGPA',
    score(q) { return /\bcgpa\b|\bgpa\b/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        if (nums.length < 1) return Clarify('cgpa', ['grade points (comma separated)'], "e.g. 'cgpa 8 9 7 8.5 9'");
        let pct = /percentage|percent|\bin\s*%\b/.test(q);
        let avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        if (pct) {
            let percentage = avg * 9.5;
            return R({
                result: `${percentage.toFixed(2)}%`, type: 'CGPA → Percentage', formula: 'CGPA × 9.5',
                calc: `${avg.toFixed(2)} × 9.5`,
                explain: `A CGPA of ${avg.toFixed(2)} converts to approximately ${percentage.toFixed(2)}% (CBSE convention).`,
            });
        }
        return R({
            result: avg.toFixed(2), type: 'CGPA (Average Grade Points)', formula: 'Σ Grade Points / n',
            calc: `${nums.join(' + ')} / ${nums.length}`,
            explain: `The CGPA is ${avg.toFixed(2)} from ${nums.length} grade points.`,
            related: [`Approx percentage: ${(avg * 9.5).toFixed(2)}%`],
        });
    }
});

/* ---- Attendance ---- */
MODULES.push({
    id: 'attendance',
    name: 'Attendance %',
    score(q) { return /attendance/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('attendance', ['classes attended', 'total classes'], "e.g. 'attendance 45 out of 60'");
        let [attended, total] = nums;
        if (attended > total) [attended, total] = [total, attended];
        let pct = (attended / total) * 100;
        let need75 = Math.max(0, Math.ceil((0.75 * total - attended) / 0.25));
        return R({
            result: `${fmtNum(pct)}%`, type: 'Attendance', formula: 'Attended / Total × 100',
            calc: `${attended} / ${total} × 100`,
            explain: `Attending ${attended} of ${total} classes yields ${fmtNum(pct)}% attendance.`,
            related: pct < 75 ? [`Need ~${need75} more consecutive classes to reach 75%`] : ['Above the standard 75% threshold ✓'],
        });
    }
});

/* ---- Ohm's Law ---- */
MODULES.push({
    id: 'ohmsLaw',
    name: "Ohm's Law",
    score(q) { return /ohm|voltage|current|resistance/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        let v = null, i = null, r = null;
        let mv = q.match(/(\d+(?:\.\d+)?)\s*(v|volts?)/); if (mv) v = parseFloat(mv[1]);
        let mi = q.match(/(\d+(?:\.\d+)?)\s*(a|amps?|amperes?)/); if (mi) i = parseFloat(mi[1]);
        let mr = q.match(/(\d+(?:\.\d+)?)\s*(ohms?|ω)/); if (mr) r = parseFloat(mr[1]);
        if (v !== null && i !== null) return R({ result: `${fmtNum(v / i)} Ω`, type: "Ohm's Law — Resistance", formula: 'R = V / I', calc: `${v} / ${i}`, explain: `With ${v}V and ${i}A current, resistance R = ${fmtNum(v / i)} Ω.` });
        if (v !== null && r !== null) return R({ result: `${fmtNum(v / r)} A`, type: "Ohm's Law — Current", formula: 'I = V / R', calc: `${v} / ${r}`, explain: `A ${v}V source across ${r}Ω draws I = ${fmtNum(v / r)} A.` });
        if (i !== null && r !== null) return R({ result: `${fmtNum(i * r)} V`, type: "Ohm's Law — Voltage", formula: 'V = I × R', calc: `${i} × ${r}`, explain: `${i}A through ${r}Ω requires V = ${fmtNum(i * r)} V.` });
        return Clarify('ohmsLaw', ['any two of Voltage (V), Current (A), Resistance (Ω)'], "e.g. '12v across 4 ohm'");
    }
});

/* ---- Geometry ---- */
MODULES.push({
    id: 'geometry',
    name: 'Geometry',
    score(q) { return /\barea\b|\bperimeter\b|\bcircumference\b|\bvolume\b/.test(q) ? 0.75 : 0; },
    run(q, nums) {
        if (/circle/.test(q)) {
            if (!nums.length) return Clarify('geometry', ['radius'], "e.g. 'area of circle radius 7'");
            let r = nums[0];
            let area = Math.PI * r * r, circ = 2 * Math.PI * r;
            if (/circumference|perimeter/.test(q)) return R({ result: fmtNum(circ), type: 'Circle Circumference', formula: '2πr', calc: `2 × π × ${r}`, explain: `Circumference of a circle with radius ${r} is ${fmtNum(circ)}.` });
            return R({ result: fmtNum(area), type: 'Circle Area', formula: 'πr²', calc: `π × ${r}²`, explain: `Area of a circle with radius ${r} is ${fmtNum(area)} sq units.`, related: [`Circumference: ${fmtNum(circ)}`] });
        }
        if (/rectangle/.test(q)) {
            if (nums.length < 2) return Clarify('geometry', ['length', 'width'], "e.g. 'area of rectangle 5 by 8'");
            let [l, w] = nums;
            if (/perimeter/.test(q)) return R({ result: fmtNum(2 * (l + w)), type: 'Rectangle Perimeter', formula: '2(l + w)', calc: `2 × (${l} + ${w})`, explain: `Perimeter = ${fmtNum(2 * (l + w))}.` });
            return R({ result: fmtNum(l * w), type: 'Rectangle Area', formula: 'l × w', calc: `${l} × ${w}`, explain: `Area of rectangle ${l}×${w} = ${fmtNum(l * w)} sq units.` });
        }
        if (/triangle/.test(q)) {
            if (nums.length < 2) return Clarify('geometry', ['base', 'height'], "e.g. 'area of triangle base 5 height 8'");
            let [b, h] = nums;
            return R({ result: fmtNum(0.5 * b * h), type: 'Triangle Area', formula: '½ × base × height', calc: `0.5 × ${b} × ${h}`, explain: `Area of triangle = ${fmtNum(0.5 * b * h)} sq units.` });
        }
        if (/square/.test(q) && nums.length) {
            let s = nums[0];
            if (/perimeter/.test(q)) return R({ result: fmtNum(4 * s), type: 'Square Perimeter', formula: '4 × side', calc: `4 × ${s}`, explain: `Perimeter = ${fmtNum(4 * s)}.` });
            return R({ result: fmtNum(s * s), type: 'Square Area', formula: 'side²', calc: `${s}²`, explain: `Area of square = ${fmtNum(s * s)} sq units.` });
        }
        if (/sphere/.test(q) && nums.length) {
            let r = nums[0];
            return R({ result: fmtNum((4 / 3) * Math.PI * r * r * r), type: 'Sphere Volume', formula: '⁴⁄₃ πr³', calc: `⁴⁄₃ × π × ${r}³`, explain: `Volume of sphere with radius ${r} = ${fmtNum((4 / 3) * Math.PI * r * r * r)}.` });
        }
        if (/cube/.test(q) && nums.length) {
            let s = nums[0];
            return R({ result: fmtNum(s * s * s), type: 'Cube Volume', formula: 'side³', calc: `${s}³`, explain: `Volume of cube = ${fmtNum(s * s * s)}.` });
        }
        return 0;
    }
});

/* ---- LCM / GCD ---- */
MODULES.push({
    id: 'lcmGcd',
    name: 'LCM / GCD',
    score(q) { return /\blcm\b|\bgcd\b|\bhcf\b|highest common|lowest common/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('lcmGcd', ['two or more integers'], "e.g. 'lcm of 12 and 18'");
        let isLcm = /\blcm\b|lowest common/.test(q);
        if (isLcm) {
            let v = nums.reduce((a, b) => lcm(a, b));
            return R({ result: String(v), type: 'LCM', formula: 'Iterative LCM', calc: `lcm(${nums.join(', ')})`, explain: `The least common multiple of ${nums.join(', ')} is ${v}.` });
        }
        let v = nums.reduce((a, b) => gcd(a, b));
        return R({ result: String(v), type: 'GCD (HCF)', formula: 'Iterative GCD', calc: `gcd(${nums.join(', ')})`, explain: `The greatest common divisor of ${nums.join(', ')} is ${v}.` });
    }
});

/* ---- Quadratic ---- */
MODULES.push({
    id: 'quadratic',
    name: 'Quadratic Equation',
    score(q) { return /quadratic|roots of/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        if (nums.length < 3) return Clarify('quadratic', ['coefficients a, b, c'], "e.g. 'roots of 1 -5 6' (means x² − 5x + 6)");
        let [a, b, c] = nums;
        let disc = b * b - 4 * a * c;
        let calc = `Discriminant = ${b}² − 4×${a}×${c} = ${disc}`;
        if (disc < 0) {
            let re = -b / (2 * a), im = Math.sqrt(-disc) / (2 * a);
            return R({
                result: `x = ${fmtNum(re)} ± ${fmtNum(im)}i`, type: 'Quadratic Roots (Complex)',
                formula: 'x = (−b ± √(b²−4ac)) / 2a', calc,
                explain: `Discriminant is negative → two complex conjugate roots.`,
            });
        }
        let s = Math.sqrt(disc);
        let x1 = (-b + s) / (2 * a), x2 = (-b - s) / (2 * a);
        return R({
            result: `x = ${fmtNum(x1)}, ${fmtNum(x2)}`, type: 'Quadratic Roots',
            formula: 'x = (−b ± √(b²−4ac)) / 2a', calc,
            explain: `The equation ${a}x² + ${b}x + ${c} = 0 has roots ${fmtNum(x1)} and ${fmtNum(x2)}.`,
        });
    }
});

/* ---- Statistics ---- */
MODULES.push({
    id: 'statistics',
    name: 'Statistics',
    score(q) { return /\bmean\b|\baverage\b|\bmedian\b|\bmode\b|standard deviation|\bvariance\b/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('statistics', ['a list of numbers (2 or more)'], "e.g. 'mean of 2,4,4,4,5,5,7,9'");
        let n = nums.length;
        let mean = nums.reduce((a, b) => a + b, 0) / n;
        let sorted = [...nums].sort((a, b) => a - b);
        let median = n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
        let freq = {}; nums.forEach(x => freq[x] = (freq[x] || 0) + 1);
        let maxFreq = Math.max(...Object.values(freq));
        let modes = Object.keys(freq).filter(k => freq[k] === maxFreq).map(Number);
        let variance = nums.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / n;
        let stddev = Math.sqrt(variance);

        let asked = /median/.test(q) ? 'median' : /mode/.test(q) ? 'mode' : /standard deviation/.test(q) ? 'stddev' : /variance/.test(q) ? 'variance' : 'mean';
        let primary = { mean: fmtNum(mean), median: fmtNum(median), mode: modes.length === n ? 'No mode' : modes.map(fmtNum).join(', '), stddev: fmtNum(stddev), variance: fmtNum(variance) }[asked];
        let label = { mean: 'Mean (Average)', median: 'Median', mode: 'Mode', stddev: 'Standard Deviation', variance: 'Variance' }[asked];

        return R({
            result: primary, type: label,
            formula: asked === 'mean' ? 'Σx / n' : asked === 'stddev' ? '√(Σ(x−mean)² / n)' : asked === 'variance' ? 'Σ(x−mean)² / n' : 'sorted middle value(s)',
            calc: `n=${n}, dataset: [${nums.join(', ')}]`,
            explain: `For the ${n} values provided: mean=${fmtNum(mean)}, median=${fmtNum(median)}, mode=${modes.length === n ? 'none' : modes.map(fmtNum).join(', ')}, std dev=${fmtNum(stddev)}, variance=${fmtNum(variance)}.`,
            related: [`Mean: ${fmtNum(mean)}`, `Median: ${fmtNum(median)}`, `Std Dev: ${fmtNum(stddev)}`],
        });
    }
});

/* ---- Permutation / Combination ---- */
MODULES.push({
    id: 'permCombo',
    name: 'Permutation / Combination',
    score(q) { return /\bchoose\b|combination of|permutation of|\bncr\b|\bnpr\b/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('permCombo', ['n and r values'], "e.g. '5 choose 2' or 'permutation of 5 and 2'");
        let [n, r] = nums;
        let isPerm = /permutation|npr/.test(q);
        let val = isPerm ? nPr(n, r) : nCr(n, r);
        return R({
            result: fmtNum(val), type: isPerm ? 'Permutation (nPr)' : 'Combination (nCr)',
            formula: isPerm ? 'n! / (n−r)!' : 'n! / (r!(n−r)!)',
            calc: `n=${n}, r=${r}`,
            explain: `${isPerm ? `The number of ways to arrange ${r} items from ${n}` : `The number of ways to choose ${r} items from ${n}`} is ${fmtNum(val)}.`,
        });
    }
});

/* ---- Probability ---- */
MODULES.push({
    id: 'probability',
    name: 'Probability',
    score(q) { return /probability/.test(q) ? 0.85 : 0; },
    run(q) {
        if (/dice|die/.test(q)) {
            if (/even number/.test(q)) return R({ result: '1/2 (0.5)', type: 'Probability', formula: 'Favourable / Total', calc: '3 / 6', explain: 'Die has 3 even faces of 6 → 1/2.' });
            if (/odd number/.test(q)) return R({ result: '1/2 (0.5)', type: 'Probability', formula: 'Favourable / Total', calc: '3 / 6', explain: 'Die has 3 odd faces of 6 → 1/2.' });
            let m = q.match(/getting a (\d)/) || q.match(/rolling a (\d)/);
            if (m) return R({ result: '1/6 (0.167)', type: 'Probability', formula: '1/6', calc: '1 of 6', explain: `Rolling a ${m[1]} on a fair die = 1/6 ≈ 0.167.` });
            return R({ result: '1/6 per face', type: 'Probability', formula: '1/6', calc: 'Equal chance per face', explain: 'A fair die has 1/6 chance per face.' });
        }
        if (/coin/.test(q)) return R({ result: '1/2 (0.5)', type: 'Probability', formula: '1/2', calc: '1 of 2', explain: 'Fair coin → 50% chance heads or tails.' });
        if (/deck|card/.test(q)) return R({ result: '1/52', type: 'Probability', formula: '1/52', calc: 'Any specific card', explain: 'A standard deck has 52 cards; each is 1/52.' });
        return Clarify('probability', ['event details (dice, coin, card, etc.)'], "e.g. 'probability of getting a 6 on a dice'");
    }
});

/* ---- Unit Conversion ---- */
MODULES.push({
    id: 'unitConvert',
    name: 'Unit Conversion',
    score(q) { return /\d\s*[a-z°/]+\s*(?:to|in|into)\s*[a-z°/]+/.test(q) ? 0.75 : 0; },
    run(q) {
        let m = q.match(/(-?\d+(?:\.\d+)?)\s*([a-z°/]+)\s*(?:to|in|into)\s*([a-z°/]+)/);
        if (!m) return 0;
        let value = parseFloat(m[1]), from = m[2], to = m[3];
        if (/^[cfk]$|celsius|fahrenheit|kelvin/.test(from) || /^[cfk]$|celsius|fahrenheit|kelvin/.test(to)) {
            let out = convertTemperature(value, from, to);
            if (out === null) return 0;
            return R({
                result: `${fmtNum(out)}°${to[0].toUpperCase()}`, type: 'Temperature Conversion',
                formula: 'Standard temperature scale conversion', calc: `${value}° ${from} → ${to}`,
                explain: `${value}° ${from} equals ${fmtNum(out)}° ${to}.`,
            });
        }
        let fromU = findUnitGroup(from), toU = findUnitGroup(to);
        if (!fromU || !toU || fromU.group !== toU.group) return Clarify('unitConvert', ['a recognised unit pair'], "e.g. '10 km to miles', '5 kg to lb', '2 l to gallon'");
        let base = value * fromU.factor;
        let result = base / toU.factor;
        return R({
            result: `${fmtNum(result)} ${to}`, type: `${fromU.group[0].toUpperCase() + fromU.group.slice(1)} Conversion`,
            formula: `value × factor(${from}) / factor(${to})`, calc: `${value} × ${fromU.factor} / ${toU.factor}`,
            explain: `${value} ${from} = ${fmtNum(result)} ${to}.`,
        });
    }
});

/* ---- Number Base / Bitwise ---- */
MODULES.push({
    id: 'numberBase',
    name: 'Number Base Conversion',
    score(q) { return /binary|hexadecimal|\bhex\b|\boctal\b|bitwise|xor|<<|>>/.test(q) ? 0.75 : 0; },
    run(q, nums) {
        let bitwise = q.match(/(-?\d+)\s*(and|or|xor)\s*(-?\d+)/);
        if (bitwise) {
            let a = parseInt(bitwise[1]), op = bitwise[2], b = parseInt(bitwise[3]);
            let val = op === 'and' ? (a & b) : op === 'or' ? (a | b) : (a ^ b);
            return R({ result: String(val), type: `Bitwise ${op.toUpperCase()}`, formula: `a ${op.toUpperCase()} b`, calc: `${a} ${op.toUpperCase()} ${b}`, explain: `${a} ${op.toUpperCase()} ${b} = ${val} (binary: ${val.toString(2)}).` });
        }
        let shift = q.match(/(-?\d+)\s*(<<|>>)\s*(-?\d+)/);
        if (shift) {
            let a = parseInt(shift[1]), op = shift[2], b = parseInt(shift[3]);
            let val = op === '<<' ? (a << b) : (a >> b);
            return R({ result: String(val), type: 'Bitwise Shift', formula: `a ${op} b`, calc: `${a} ${op} ${b}`, explain: `${a} ${op} ${b} = ${val}.` });
        }
        let toBinary = /binary/.test(q);
        let toHex = /hex/.test(q);
        let toOctal = /octal/.test(q);
        let fromBinary = /\bfrom binary\b|binary to decimal|binary to hex/.test(q);
        if (nums.length) {
            let n = nums[0];
            if (fromBinary) { let dec = parseInt(String(n), 2); return R({ result: String(dec), type: 'Binary → Decimal', formula: 'parseInt(binary, 2)', calc: `${n} (base 2) → decimal`, explain: `Binary ${n} = decimal ${dec}.` }); }
            if (toBinary) return R({ result: n.toString(2), type: 'Decimal → Binary', formula: 'n.toString(2)', calc: `${n} → base 2`, explain: `${n} in binary is ${n.toString(2)}.` });
            if (toHex) return R({ result: n.toString(16).toUpperCase(), type: 'Decimal → Hexadecimal', formula: 'n.toString(16)', calc: `${n} → base 16`, explain: `${n} in hex is ${n.toString(16).toUpperCase()}.` });
            if (toOctal) return R({ result: n.toString(8), type: 'Decimal → Octal', formula: 'n.toString(8)', calc: `${n} → base 8`, explain: `${n} in octal is ${n.toString(8)}.` });
        }
        return 0;
    }
});

/* ---- Scientific ---- */
MODULES.push({
    id: 'scientific',
    name: 'Scientific',
    score(q) { return /sqrt|square root|cube root|\bpower\b|\^|\blog\b|\bln\b|\bsin\b|\bcos\b|\btan\b|factorial|\d\s*!/.test(q) ? 0.75 : 0; },
    run(q) {
        let m;
        if ((m = q.match(/factorial of (\d+)/) || q.match(/(\d+)\s*!/))) {
            let n = parseInt(m[1]);
            return R({ result: fmtNum(factorial(n)), type: 'Factorial', formula: 'n!', calc: `${n}!`, explain: `${n}! = ${fmtNum(factorial(n))}.` });
        }
        if ((m = q.match(/(?:square root|sqrt)(?:\s*of)?\s*(\d+(?:\.\d+)?)/))) {
            let n = parseFloat(m[1]);
            return R({ result: fmtNum(Math.sqrt(n)), type: 'Square Root', formula: '√n', calc: `√${n}`, explain: `Square root of ${n} is ${fmtNum(Math.sqrt(n))}.` });
        }
        if ((m = q.match(/cube root(?:\s*of)?\s*(\d+(?:\.\d+)?)/))) {
            let n = parseFloat(m[1]);
            return R({ result: fmtNum(Math.cbrt(n)), type: 'Cube Root', formula: 'n^(1/3)', calc: `∛${n}`, explain: `Cube root of ${n} is ${fmtNum(Math.cbrt(n))}.` });
        }
        if ((m = q.match(/(\d+(?:\.\d+)?)\s*power\s*(\d+(?:\.\d+)?)/) || q.match(/(\d+(?:\.\d+)?)\s*\^\s*(\d+(?:\.\d+)?)/))) {
            let base = parseFloat(m[1]), exp = parseFloat(m[2]);
            return R({ result: fmtNum(Math.pow(base, exp)), type: 'Power / Exponent', formula: 'baseᵉˣᵖ', calc: `${base}^${exp}`, explain: `${base}^${exp} = ${fmtNum(Math.pow(base, exp))}.` });
        }
        if ((m = q.match(/\bln\s*(\d+(?:\.\d+)?)/))) {
            let n = parseFloat(m[1]);
            return R({ result: fmtNum(Math.log(n)), type: 'Natural Log (ln)', formula: 'ln(n)', calc: `ln(${n})`, explain: `ln(${n}) = ${fmtNum(Math.log(n))}.` });
        }
        if ((m = q.match(/\blog\s*(\d+(?:\.\d+)?)/))) {
            let n = parseFloat(m[1]);
            return R({ result: fmtNum(Math.log10(n)), type: 'Log₁₀', formula: 'log₁₀(n)', calc: `log(${n})`, explain: `log₁₀(${n}) = ${fmtNum(Math.log10(n))}.` });
        }
        if ((m = q.match(/\b(sin|cos|tan)\s*(\d+(?:\.\d+)?)/))) {
            let fn = m[1], deg = parseFloat(m[2]);
            let rad = deg * Math.PI / 180;
            let val = fn === 'sin' ? Math.sin(rad) : fn === 'cos' ? Math.cos(rad) : Math.tan(rad);
            return R({ result: fmtNum(val, 4), type: `Trigonometry (${fn})`, formula: `${fn}(degrees × π/180)`, calc: `${fn}(${deg}°)`, explain: `${fn}(${deg}°) = ${fmtNum(val, 4)}.` });
        }
        return 0;
    }
});

/* ---- Inflation ---- */
MODULES.push({
    id: 'inflation',
    name: 'Inflation',
    score(q) { return /inflation/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let rate = extractRate(q);
        let years = extractYears(q);
        let amount = pickPrincipal(nums, [rate, years].filter(v => v !== null));
        if (amount === null || rate === null || years === null) return Clarify('inflation', ['the amount', 'inflation rate (%)', 'number of years'], "e.g. 'future cost of 10000 after 5 years at 6% inflation'");
        let isFuture = !/today's value|present value|real value/.test(q);
        if (isFuture) {
            let future = amount * Math.pow(1 + rate / 100, years);
            return R({
                result: inr(future), type: 'Future Cost (Inflation-Adjusted)', formula: 'FV = P × (1 + rate/100)ᵗ',
                calc: `${amount} × (1 + ${rate}/100)^${years}`, explain: `${inr(amount)} today will cost about ${inr(future)} in ${years} years at ${rate}% inflation.`,
            });
        }
        let present = amount / Math.pow(1 + rate / 100, years);
        return R({
            result: inr(present), type: "Today's Value", formula: 'PV = FV / (1 + rate/100)ᵗ',
            calc: `${amount} / (1 + ${rate}/100)^${years}`, explain: `${inr(amount)} in ${years} years is worth ${inr(present)} today at ${rate}% inflation.`,
        });
    }
});

/* ---- Currency Conversion (async, live rate) ---- */
MODULES.push({
    id: 'currency',
    name: 'Currency Conversion',
    score(q) { return /\b(usd|inr|eur|gbp|jpy|aud|cad)\b.*\bto\b|currency|exchange rate/.test(q) ? 0.8 : 0; },
    async: true,
    run(q, nums) {
        let m = q.match(/\b([a-z]{3})\b\s*(?:to|in|into)\s*\b([a-z]{3})\b/);
        if (!m) return Clarify('currency', ['currencies to convert'], "e.g. '100 usd to inr'");
        let from = m[1], to = m[2];
        let amount = nums.length ? nums[0] : 1;
        return fetch(`https://open.er-api.com/v6/latest/${from.toUpperCase()}`)
            .then(res => res.json())
            .then(data => {
                if (data.result !== 'success' || !data.rates || !data.rates[to.toUpperCase()]) throw new Error('rate unavailable');
                let rate = data.rates[to.toUpperCase()];
                let converted = amount * rate;
                return R({
                    result: `${fmtNum(converted)} ${to.toUpperCase()}`, type: 'Currency Conversion (Live)',
                    formula: 'Amount × Exchange Rate', calc: `${amount} × ${fmtNum(rate, 4)}`,
                    explain: `${amount} ${from.toUpperCase()} ≈ ${fmtNum(converted)} ${to.toUpperCase()} at 1 ${from.toUpperCase()} = ${fmtNum(rate, 4)} ${to.toUpperCase()}.`,
                    warnings: ['Live mid-market rate — actual bank rates include a margin.'],
                });
            })
            .catch(() => Clarify('currency', [], "Couldn't fetch live rates — check your internet and try again."));
    }
});

/* ---- Salary / CTC ---- */
MODULES.push({
    id: 'salary',
    name: 'Salary / CTC',
    score(q) { return /in.?hand|take.?home|\bctc\b/.test(q) ? 0.8 : 0; },
    run(q, nums) {
        let ctc = pickPrincipal(nums);
        if (ctc === null) return Clarify('salary', ['the annual CTC amount'], "e.g. 'in hand salary for CTC 10 lakh'");
        let basic = ctc * 0.4;
        let employeePF = Math.min(basic * 0.12, 21600);
        let professionalTax = 2400;
        let approxTax = ctc > 700000 ? ctc * 0.08 : ctc > 500000 ? ctc * 0.03 : 0;
        let inHandAnnual = ctc - employeePF - professionalTax - approxTax;
        return R({
            result: inr(Math.round(inHandAnnual / 12)) + '/month', type: 'Estimated In-Hand Salary',
            formula: '(CTC − PF − Prof. Tax − Approx. Tax) / 12',
            calc: `(${fmtNum(ctc)} − ${fmtNum(employeePF)} − ${professionalTax} − ${fmtNum(approxTax)}) / 12`,
            explain: `For a CTC of ${inr(ctc)}, monthly in-hand ≈ ${inr(Math.round(inHandAnnual / 12))} after typical PF and tax.`,
            related: [`Estimated annual in-hand: ${inr(Math.round(inHandAnnual))}`],
            warnings: ['Rough estimate — actual depends on tax regime, exemptions, HRA, etc.'],
        });
    }
});

/* ---- Fibonacci ---- */
MODULES.push({
    id: 'fibonacci',
    name: 'Fibonacci',
    score(q) { return /fibonacci/.test(q) ? 0.95 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('fibonacci', ['n (how many terms)'], "e.g. 'fibonacci 10 terms'");
        let n = Math.min(30, Math.max(1, Math.round(nums[0])));
        let seq = [0, 1];
        while (seq.length < n) seq.push(seq[seq.length - 1] + seq[seq.length - 2]);
        seq = seq.slice(0, n);
        return R({
            result: seq.join(', '), type: 'Fibonacci Sequence', formula: 'F(n) = F(n−1) + F(n−2)',
            calc: `First ${n} terms`, explain: `The first ${n} Fibonacci numbers are shown above.`,
        });
    }
});

/* ---- Math Expression fallback ---- */
MODULES.push({
    id: 'mathExpr',
    name: 'Math Expression',
    score(q) {
        let cleaned = q.replace(/[^\d+\-*/().\s]/g, '');
        return cleaned.trim().length > 2 && /[+\-*/]/.test(cleaned) ? 0.4 : 0;
    },
    run(q) {
        let cleaned = q.replace(/[^\d+\-*/().\s]/g, '');
        try {
            // `cleaned` is pre-stripped above to only digits/()+-*/. characters,
            // so this only ever evaluates a plain arithmetic expression.
            // eslint-disable-next-line no-new-func
            let val = new Function(`"use strict"; return (${cleaned})`)();
            if (val !== undefined && !isNaN(val) && isFinite(val)) {
                return R({
                    result: fmtNum(val), type: 'Mathematical Expression', formula: 'Direct evaluation',
                    calc: cleaned.trim(), explain: `${cleaned.trim()} = ${fmtNum(val)}.`,
                });
            }
        } catch (e) { /* ignore */ }
        return 0;
    }
});

/* ===== NEW FEATURE START ===== (Batch 1 engine expansion — 14 new
   calculator intents added on top of the original 40. Each follows the
   exact same MODULES.push({ id, name, score, run }) pattern used by every
   module above, so they're picked up automatically by the existing
   classify() scan/dispatch loop — nothing above this line, and nothing in
   classify() itself, was changed to add these. Purely additive. */

function extractLabeledNum(q, labelPatterns) {
    for (const p of labelPatterns) {
        const m = q.match(p);
        if (m) return parseFloat(m[1]);
    }
    return null;
}
function extractPercents(q) {
    const out = [];
    const re = /(\d+(?:\.\d+)?)\s*(?:%|percent)/g;
    let m;
    while ((m = re.exec(q))) out.push(parseFloat(m[1]));
    return out;
}

/* ---- Trigonometry ---- */
MODULES.push({
    id: 'trigonometry',
    name: 'Trigonometry',
    score(q) { return /\b(sin|cos|tan|asin|acos|atan|arcsin|arccos|arctan|sine|cosine|tangent)\b/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('trigonometry', ['an angle or ratio value'], "e.g. 'sin of 30 degrees'");
        let fn = null;
        if (/\b(arcsin|asin)\b/.test(q)) fn = 'asin';
        else if (/\b(arccos|acos)\b/.test(q)) fn = 'acos';
        else if (/\b(arctan|atan)\b/.test(q)) fn = 'atan';
        else if (/\b(sin|sine)\b/.test(q)) fn = 'sin';
        else if (/\b(cos|cosine)\b/.test(q)) fn = 'cos';
        else if (/\b(tan|tangent)\b/.test(q)) fn = 'tan';
        if (!fn) return 0;
        let x = nums[0];
        if (fn[0] === 'a') {
            if (x < -1 || x > 1) return Clarify('trigonometry', [], `${fn} needs a ratio between -1 and 1.`);
            let rad = Math[fn](x), deg = rad * 180 / Math.PI;
            return R({
                result: `${fmtNum(deg)}°`, type: 'Inverse Trigonometry',
                formula: `${fn}(x) → degrees`, calc: `${fn}(${x}) = ${fmtNum(rad, 4)} rad`,
                explain: `The angle whose ${fn.slice(1)} is ${x} is ${fmtNum(deg)}°.`,
            });
        }
        let useRadian = /radian/.test(q);
        let rad = useRadian ? x : x * Math.PI / 180;
        let val = Math[fn](rad);
        return R({
            result: fmtNum(val, 4), type: 'Trigonometry',
            formula: `${fn}(θ)`, calc: `${fn}(${x}${useRadian ? ' rad' : '°'})`,
            explain: `${fn}(${x}${useRadian ? ' radians' : '°'}) = ${fmtNum(val, 4)}.`,
        });
    }
});

/* ---- Power & Roots ---- */
MODULES.push({
    id: 'powerRoot',
    name: 'Power & Roots',
    score(q) { return /square root|cube root|nth root|to the power|power of/.test(q) || /\d\s*\^\s*\d/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        if (/square root/.test(q)) {
            if (!nums.length) return Clarify('powerRoot', ['a number'], "e.g. 'square root of 144'");
            let x = nums[0];
            if (x < 0) return Clarify('powerRoot', [], 'The square root of a negative number is not a real number.');
            let v = Math.sqrt(x);
            return R({ result: fmtNum(v, 4), type: 'Square Root', formula: '√x', calc: `√${x}`, explain: `The square root of ${x} is ${fmtNum(v, 4)}.` });
        }
        if (/cube root/.test(q)) {
            if (!nums.length) return Clarify('powerRoot', ['a number'], "e.g. 'cube root of 27'");
            let x = nums[0], v = Math.cbrt(x);
            return R({ result: fmtNum(v, 4), type: 'Cube Root', formula: '∛x', calc: `∛${x}`, explain: `The cube root of ${x} is ${fmtNum(v, 4)}.` });
        }
        if (/nth root/.test(q) && nums.length >= 2) {
            let [x, n] = nums, v = Math.pow(x, 1 / n);
            return R({ result: fmtNum(v, 4), type: 'Nth Root', formula: 'x^(1/n)', calc: `${x}^(1/${n})`, explain: `The ${n}th root of ${x} is ${fmtNum(v, 4)}.` });
        }
        if ((/to the power|power of/.test(q) || /\^/.test(q)) && nums.length >= 2) {
            let [base, exp] = nums, v = Math.pow(base, exp);
            return R({ result: fmtNum(v), type: 'Exponentiation', formula: 'base^exponent', calc: `${base}^${exp}`, explain: `${base} raised to the power ${exp} is ${fmtNum(v)}.` });
        }
        return 0;
    }
});

/* ---- Logarithm ---- */
MODULES.push({
    id: 'logarithm',
    name: 'Logarithm',
    score(q) { return /\blog\b|\bln\b|logarithm/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('logarithm', ['a number'], "e.g. 'log of 100' or 'log base 2 of 8'");
        if (/\bln\b/.test(q)) {
            let x = nums[0], v = Math.log(x);
            return R({ result: fmtNum(v, 4), type: 'Natural Logarithm', formula: 'ln(x)', calc: `ln(${x})`, explain: `The natural log of ${x} is ${fmtNum(v, 4)}.` });
        }
        let baseMatch = q.match(/log base (\d+(?:\.\d+)?)/);
        if (baseMatch && nums.length >= 2) {
            let base = parseFloat(baseMatch[1]);
            let other = nums.find(n => n !== base);
            let x = other !== undefined ? other : nums[nums.length - 1];
            let v = Math.log(x) / Math.log(base);
            return R({ result: fmtNum(v, 4), type: 'Logarithm', formula: 'log_b(x) = ln(x)/ln(b)', calc: `log base ${base} of ${x}`, explain: `log base ${base} of ${x} is ${fmtNum(v, 4)}.` });
        }
        let x = nums[0], v = Math.log10(x);
        return R({ result: fmtNum(v, 4), type: 'Logarithm (base 10)', formula: 'log₁₀(x)', calc: `log(${x})`, explain: `The base-10 log of ${x} is ${fmtNum(v, 4)}.` });
    }
});

/* ---- Prime Number Check ---- */
MODULES.push({
    id: 'primeCheck',
    name: 'Prime Number Check',
    score(q) { return /\bprime\b/.test(q) ? 0.8 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('primeCheck', ['a whole number'], "e.g. 'is 17 a prime number'");
        let n = Math.round(nums[0]);
        if (n < 2) {
            return R({ result: 'No', type: 'Prime Check', formula: 'Trial division', calc: `${n} < 2`, explain: `${n} is not a prime number (primes are greater than 1).` });
        }
        let isPrime = true;
        for (let i = 2; i * i <= n; i++) { if (n % i === 0) { isPrime = false; break; } }
        return R({
            result: isPrime ? 'Yes, prime' : 'No, not prime', type: 'Prime Check',
            formula: 'Trial division up to √n', calc: `Checked divisibility up to √${n} ≈ ${fmtNum(Math.sqrt(n), 1)}`,
            explain: `${n} is ${isPrime ? '' : 'not '}a prime number.`,
        });
    }
});

/* ---- Factors ---- */
MODULES.push({
    id: 'factors',
    name: 'Factors',
    score(q) { return /\bfactors? of\b/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('factors', ['a whole number'], "e.g. 'factors of 36'");
        let n = Math.abs(Math.round(nums[0]));
        if (n === 0) return Clarify('factors', [], 'Factors are undefined for 0.');
        if (n > 1000000) return Clarify('factors', [], 'Please use a number under 1,000,000 for factor listing.');
        let list = [];
        for (let i = 1; i <= n; i++) if (n % i === 0) list.push(i);
        return R({
            result: list.join(', '), type: 'Factors',
            formula: 'Divisors of n', calc: `Divisors of ${n}`,
            explain: `${n} has ${list.length} factor${list.length === 1 ? '' : 's'}: ${list.join(', ')}.`,
        });
    }
});

/* ---- Factorial ---- */
MODULES.push({
    id: 'factorialCalc',
    name: 'Factorial',
    score(q) { return /factorial/.test(q) || /\d\s*!/.test(q) ? 0.8 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('factorialCalc', ['a whole number'], "e.g. 'factorial of 5' or '5!'");
        let n = Math.round(nums[0]);
        if (n < 0) return Clarify('factorialCalc', [], 'Factorial is only defined for non-negative integers.');
        if (n > 170) return Clarify('factorialCalc', [], 'That number is too large to compute exactly (factorial overflows past 170!).');
        let v = factorial(n);
        return R({ result: fmtNum(v), type: 'Factorial', formula: 'n!', calc: `${n}!`, explain: `${n}! = ${fmtNum(v)}.` });
    }
});

/* ---- Roman Numerals ---- */
const ROMAN_TABLE = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
];
function toRoman(num) {
    let result = '';
    for (const [val, sym] of ROMAN_TABLE) { while (num >= val) { result += sym; num -= val; } }
    return result;
}
function fromRoman(str) {
    const map = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
    let total = 0;
    for (let i = 0; i < str.length; i++) {
        let cur = map[str[i]], next = map[str[i + 1]];
        if (next && cur < next) total -= cur; else total += cur;
    }
    return total;
}
MODULES.push({
    id: 'romanNumeral',
    name: 'Roman Numerals',
    score(q) { return /\broman\b/.test(q) ? 0.85 : 0; },
    run(q, nums) {
        if (nums.length) {
            let n = Math.round(nums[0]);
            if (n < 1 || n > 3999) return Clarify('romanNumeral', [], 'Roman numerals here are supported for 1–3999.');
            let roman = toRoman(n);
            return R({ result: roman, type: 'Number → Roman Numeral', formula: 'Roman numeral encoding', calc: `${n} → ${roman}`, explain: `${n} in Roman numerals is ${roman}.` });
        }
        let romanMatch = q.match(/\b([ivxlcdm]{2,})\b/i);
        if (romanMatch) {
            let roman = romanMatch[1].toUpperCase();
            let val = fromRoman(roman);
            if (val <= 0) return Clarify('romanNumeral', [], "Couldn't parse that as a valid Roman numeral.");
            return R({ result: String(val), type: 'Roman Numeral → Number', formula: 'Roman numeral decoding', calc: `${roman} → ${val}`, explain: `${roman} in Roman numerals is ${val}.` });
        }
        return Clarify('romanNumeral', ['a number (1–3999) or a Roman numeral'], "e.g. 'roman numeral for 49'");
    }
});

/* ---- Tip Calculator ---- */
MODULES.push({
    id: 'tipCalculator',
    name: 'Tip Calculator',
    score(q) { return /\btip\b/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('tipCalculator', ['bill amount', 'tip percentage'], "e.g. 'tip on 500 at 10%' or 'split 800 tip 15% between 4 people'");
        let bill = nums[0];
        let pctCandidate = nums.slice(1).find(n => n <= 100);
        let pct = pctCandidate !== undefined ? pctCandidate : nums[1];
        let tip = bill * pct / 100;
        let total = bill + tip;
        let peopleMatch = q.match(/(\d+)\s*(people|persons|friends|guys)/);
        if (peopleMatch) {
            let people = parseInt(peopleMatch[1], 10);
            let perPerson = total / people;
            return R({
                result: inr(perPerson) + ' per person', type: 'Tip Split',
                formula: '(Bill + Tip) / People', calc: `(${bill} + ${fmtNum(tip)}) / ${people}`,
                explain: `A ${pct}% tip on ${inr(bill)} is ${inr(tip)}, making the total ${inr(total)} — split ${people} ways that's ${inr(perPerson)} each.`,
            });
        }
        return R({
            result: inr(total), type: 'Tip Calculation',
            formula: 'Bill + (Bill × Tip%)', calc: `${bill} + (${bill} × ${pct}%)`,
            explain: `A ${pct}% tip on ${inr(bill)} is ${inr(tip)}, making the total ${inr(total)}.`,
            related: [`Tip amount: ${inr(tip)}`],
        });
    }
});

/* ---- Speed, Distance & Time ---- */
MODULES.push({
    id: 'speedDistanceTime',
    name: 'Speed, Distance & Time',
    score(q) { return /\bspeed\b/.test(q) && /\bdistance\b|\btime\b/.test(q) ? 0.82 : 0; },
    run(q, nums) {
        if (nums.length < 2) return Clarify('speedDistanceTime', ['two of: speed, distance, time'], "e.g. 'speed if distance 120 km and time 2 hours'");
        let [a, b] = nums;
        if (/find distance|distance if|distance =/.test(q)) {
            let dist = a * b;
            return R({ result: fmtNum(dist), type: 'Distance', formula: 'Distance = Speed × Time', calc: `${a} × ${b}`, explain: `Speed ${a} × time ${b} = distance ${fmtNum(dist)} (matching units).` });
        }
        if (/find time|time if|time =/.test(q)) {
            let time = a / b;
            return R({ result: fmtNum(time), type: 'Time', formula: 'Time = Distance / Speed', calc: `${a} / ${b}`, explain: `Distance ${a} ÷ speed ${b} = time ${fmtNum(time)} (matching units).` });
        }
        let speed = a / b;
        return R({ result: fmtNum(speed), type: 'Speed', formula: 'Speed = Distance / Time', calc: `${a} / ${b}`, explain: `Distance ${a} ÷ time ${b} = speed ${fmtNum(speed)} (matching units, e.g. km/h).` });
    }
});

/* ---- Break-Even Point ---- */
MODULES.push({
    id: 'breakEven',
    name: 'Break-Even Point',
    score(q) { return /break.?even/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        let fixed = extractLabeledNum(q, [/fixed cost[^\d]*(\d+(?:\.\d+)?)/, /(\d+(?:\.\d+)?)[^\d]*fixed cost/]);
        let price = extractLabeledNum(q, [/selling price[^\d]*(\d+(?:\.\d+)?)/, /\bprice[^\d]*(\d+(?:\.\d+)?)/]);
        let variable = extractLabeledNum(q, [/variable cost[^\d]*(\d+(?:\.\d+)?)/, /(\d+(?:\.\d+)?)[^\d]*variable cost/]);
        if ((fixed === null || price === null || variable === null) && nums.length >= 3) {
            [fixed, price, variable] = [nums[0], nums[1], nums[2]];
        }
        if (fixed === null || price === null || variable === null) {
            return Clarify('breakEven', ['fixed cost', 'selling price per unit', 'variable cost per unit'], "e.g. 'break even point fixed cost 10000 price 50 variable cost 20'");
        }
        if (price <= variable) return Clarify('breakEven', [], 'Selling price must be greater than variable cost per unit for a break-even point to exist.');
        let units = fixed / (price - variable);
        return R({
            result: `${fmtNum(Math.ceil(units))} units`, type: 'Break-Even Point',
            formula: 'Fixed Cost / (Price − Variable Cost)', calc: `${fixed} / (${price} − ${variable})`,
            explain: `You need to sell about ${fmtNum(Math.ceil(units))} units to cover fixed costs of ${inr(fixed)} at a contribution margin of ${inr(price - variable)} per unit.`,
        });
    }
});

/* ---- Successive Discounts ---- */
MODULES.push({
    id: 'successiveDiscount',
    name: 'Successive Discounts',
    score(q) { return /discount/.test(q) && extractPercents(q).length >= 2 ? 0.93 : 0; },
    run(q, nums) {
        let percents = extractPercents(q);
        let amount = nums.find(n => !percents.includes(n));
        if (percents.length < 2 || amount === undefined) {
            return Clarify('successiveDiscount', ['an amount', 'two or more discount percentages'], "e.g. 'successive discount of 10% and 20% on 1000'");
        }
        let result = amount;
        let steps = [];
        percents.forEach(p => {
            let reduced = result * (1 - p / 100);
            steps.push(`${fmtNum(result)} − ${p}% = ${fmtNum(reduced)}`);
            result = reduced;
        });
        let totalDiscountPct = (1 - result / amount) * 100;
        return R({
            result: inr(result), type: 'Successive Discounts',
            formula: 'Apply each discount in sequence to the reduced amount', calc: steps.join(' → '),
            explain: `Applying ${percents.map(p => p + '%').join(' then ')} successively on ${inr(amount)} gives a final price of ${inr(result)} — equivalent to a single ${fmtNum(totalDiscountPct)}% discount.`,
        });
    }
});

/* ---- Number to Words (Indian numbering system) ---- */
const NUM_ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const NUM_TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function twoDigitWords(n) {
    if (n < 20) return NUM_ONES[n];
    return NUM_TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + NUM_ONES[n % 10] : '');
}
function threeDigitWords(n) {
    let s = '';
    if (n >= 100) { s += NUM_ONES[Math.floor(n / 100)] + ' Hundred'; n %= 100; if (n) s += ' '; }
    if (n) s += twoDigitWords(n);
    return s;
}
function numberToIndianWords(n) {
    if (n === 0) return 'Zero';
    let neg = n < 0; n = Math.abs(Math.round(n));
    let crore = Math.floor(n / 10000000); n %= 10000000;
    let lakh = Math.floor(n / 100000); n %= 100000;
    let thousand = Math.floor(n / 1000); n %= 1000;
    let rest = n;
    let parts = [];
    if (crore) parts.push(threeDigitWords(crore) + ' Crore');
    if (lakh) parts.push(threeDigitWords(lakh) + ' Lakh');
    if (thousand) parts.push(threeDigitWords(thousand) + ' Thousand');
    if (rest) parts.push(threeDigitWords(rest));
    return (neg ? 'Minus ' : '') + parts.join(' ');
}
MODULES.push({
    id: 'numberToWords',
    name: 'Number to Words',
    score(q) { return /in words|to words|spell out/.test(q) ? 0.88 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('numberToWords', ['a number'], "e.g. '1234 in words'");
        let n = nums[0];
        if (Math.abs(n) >= 1e15) return Clarify('numberToWords', [], 'That number is too large to spell out here.');
        let words = numberToIndianWords(n);
        return R({ result: words, type: 'Number in Words', formula: 'Indian numbering system (crore/lakh/thousand)', calc: `${n} → words`, explain: `${fmtNum(n)} in words (Indian numbering system) is "${words}".` });
    }
});

/* ---- Leap Year Check ---- */
MODULES.push({
    id: 'leapYear',
    name: 'Leap Year Check',
    score(q) { return /leap year/.test(q) ? 0.9 : 0; },
    run(q, nums) {
        if (!nums.length) return Clarify('leapYear', ['a year'], "e.g. 'is 2024 a leap year'");
        let y = Math.round(nums[0]);
        let isLeap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
        return R({
            result: isLeap ? 'Yes, leap year' : 'No, not a leap year', type: 'Leap Year Check',
            formula: 'Divisible by 4, not by 100 unless also by 400', calc: `${y} % 4 = ${y % 4}, % 100 = ${y % 100}, % 400 = ${y % 400}`,
            explain: `${y} is ${isLeap ? '' : 'not '}a leap year.`,
        });
    }
});

/* ---- Time Duration Add/Subtract ---- */
function extractDurationsMinutes(q) {
    const results = [];
    const re = /(\d+(?:\.\d+)?)\s*(hours?|hrs?|h)\b(?:\s*(?:and)?\s*(\d+(?:\.\d+)?)\s*(minutes?|mins?|m)\b)?|(\d+(?:\.\d+)?)\s*(minutes?|mins?)\b/g;
    let m;
    while ((m = re.exec(q))) {
        if (m[1]) {
            let hrs = parseFloat(m[1]);
            let mins = m[3] ? parseFloat(m[3]) : 0;
            results.push(hrs * 60 + mins);
        } else if (m[5]) {
            results.push(parseFloat(m[5]));
        }
    }
    return results;
}
MODULES.push({
    id: 'timeDurationAdd',
    name: 'Time Duration Add/Subtract',
    score(q) { return (/\bhours?\b|\bhrs?\b/.test(q) && /\bminutes?\b|\bmins?\b/.test(q)) && /add|plus|subtract|minus|total|combined/.test(q) ? 0.85 : 0; },
    run(q) {
        let durations = extractDurationsMinutes(q);
        if (durations.length < 2) return Clarify('timeDurationAdd', ['two or more durations (hours/minutes)'], "e.g. 'add 2 hours 30 minutes and 1 hour 45 minutes'");
        let isSubtract = /subtract|minus/.test(q);
        let total = isSubtract
            ? durations[0] - durations.slice(1).reduce((a, b) => a + b, 0)
            : durations.reduce((a, b) => a + b, 0);
        let neg = total < 0;
        total = Math.abs(total);
        let h = Math.floor(total / 60), m = Math.round(total % 60);
        return R({
            result: `${neg ? '-' : ''}${h}h ${m}m`, type: 'Duration Total',
            formula: isSubtract ? 'First duration − remaining durations' : 'Sum of durations',
            calc: durations.map(d => `${Math.floor(d / 60)}h ${Math.round(d % 60)}m`).join(isSubtract ? ' − ' : ' + '),
            explain: `${isSubtract ? 'Subtracting' : 'Adding'} the given durations gives a total of ${neg ? '-' : ''}${h} hour(s) ${m} minute(s).`,
        });
    }
});
/* ===== NEW FEATURE END ===== */

/* ---------------------------------------------------------------
   ENHANCEMENT LAYER  (added on top of the existing engine — nothing
   above this block is modified. Everything here is additive: it can
   only turn a previously-"unknown" query into a recognised one, or
   add extra metadata to an already-successful response. It never
   changes the result of a query that already worked.)
   --------------------------------------------------------------- */

/* ===== NEW FEATURE START ===== (rotating reply banks for small talk — used by
   the expanded smallTalk module above and the fallback/unknown handling below,
   so common chit-chat feels less repetitive/robotic without any external AI call) */
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

const GREETING_REPLIES = [
    "Hey there! 👋 I'm your smartcalcAi. Ask me anything — finance, health, math, unit conversion or just chat.",
    "Hi! Ready when you are — throw a calculation at me, or just say hi.",
    "Hello! 😊 What can I work out for you today?",
    "Hey! I speak calculator, and a bit of small talk too. What's up?",
];
const THANKS_REPLIES = [
    "You're welcome! Happy to help. Anything else?",
    "Anytime! 🙂 Got another one for me?",
    "No problem at all — that's what I'm here for.",
    "Glad that helped!",
];
const BYE_REPLIES = [
    "Goodbye! Come back whenever you need to crunch numbers. 👋",
    "See you later! I'll be right here.",
    "Bye! Take care.",
    "Catch you next time 👋",
];
const JOKES = [
    "Why did the calculator break up with the abacus? It just couldn't count on it anymore. 😄",
    "I told my computer I needed a break, and now it won't stop sending me KitKats.",
    "Why was the equal sign so humble? Because it knew it wasn't less than or greater than anyone else.",
    "Parallel lines have so much in common. It's a shame they'll never meet.",
    "Why do programmers prefer dark mode? Because light attracts bugs.",
];
const COMPLIMENT_REPLIES = [
    "Aw, thank you! 😊 I try my best to get every calculation right.",
    "That means a lot — glad I could help!",
    "You're too kind! Let's keep the streak going — what's next?",
];
const INSULT_REPLIES = [
    "Fair enough — I'm always learning. Give me another shot?",
    "Ouch 😅 Tell me what went wrong and I'll try to do better.",
    "Noted. Let's try again — what would you like calculated?",
];
const REACTION_REPLIES = [
    "Right? 😄 Anything else you'd like calculated?",
    "Glad you think so! What's next?",
    "😊 Let me know what else you need.",
];
const ACK_REPLIES = [
    "👍 Let me know if you need anything calculated.",
    "Sounds good — I'm here if you need me.",
    "Got it 🙂",
];

// Varied fallback lines for when nothing matches, so the "unknown" response
// doesn't always read as the exact same canned sentence.
const FALLBACK_MESSAGES = [
    "I couldn't quite work that one out. Try rephrasing, or tap an example:",
    "Hmm, that's outside what I can calculate right now. Here's what I can help with:",
    "I'm not sure I follow — could you rephrase, or try one of these:",
    "That didn't match anything I know how to compute. Maybe try:",
    "I couldn't find a calculation for that. Here are a few things I'm good at:",
];
/* ===== NEW FEATURE END ===== */

/* ---- Levenshtein edit distance (typo correction + "did you mean") ---- */
function levenshtein(a, b) {
    if (a === b) return 0;
    const m = a.length, n = b.length;
    if (!m) return n;
    if (!n) return m;
    let prev = Array(n + 1).fill(0).map((_, i) => i);
    for (let i = 1; i <= m; i++) {
        let curr = [i];
        for (let j = 1; j <= n; j++) {
            curr[j] = a[i - 1] === b[j - 1]
                ? prev[j - 1]
                : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
        }
        prev = curr;
    }
    return prev[n];
}

/* ---- Domain vocabulary used for fuzzy correction. Curated from the
   module keywords already used throughout this file, so fuzzy-matching
   only ever nudges a word toward a term this engine already understands
   — it can never invent a new meaning. Small, safe, high-precision set. */
const DOMAIN_VOCAB = [
    'percent', 'percentage', 'discount', 'gst', 'emi', 'loan', 'interest', 'compound',
    'simple', 'sip', 'fd', 'rd', 'deposit', 'bmi', 'weight', 'water', 'calories', 'bmr',
    'profit', 'loss', 'margin', 'markup', 'cagr', 'roi', 'depreciation', 'cgpa', 'gpa',
    'attendance', 'ohm', 'voltage', 'current', 'resistance', 'area', 'perimeter',
    'circumference', 'volume', 'circle', 'rectangle', 'triangle', 'square', 'sphere',
    'cube', 'lcm', 'gcd', 'hcf', 'quadratic', 'mean', 'average', 'median', 'mode',
    'variance', 'deviation', 'combination', 'permutation', 'probability', 'binary',
    'hexadecimal', 'octal', 'factorial', 'sqrt', 'root', 'power', 'log', 'sin', 'cos',
    'tan', 'inflation', 'currency', 'salary', 'fibonacci', 'kilogram', 'kilometer',
    'celsius', 'fahrenheit', 'kelvin', 'today', 'yesterday', 'tomorrow', 'age', 'date',
];

// Dynamic alias registry — lets new synonyms be registered at runtime
// (e.g. by a future admin panel) without editing SPELL_MAP/SYNONYM_MAP.
// Empty by default, so it changes nothing until something calls addAlias().
const DYNAMIC_ALIASES = {};
function addAlias(word, canonical) {
    if (typeof word === 'string' && typeof canonical === 'string' && word.trim() && canonical.trim()) {
        DYNAMIC_ALIASES[word.toLowerCase().trim()] = canonical.toLowerCase().trim();
    }
}

// Small stopword set we never fuzzy-correct (prevents corrupting structural
// English/Hindi words like "of", "is", "to", "for" into a domain term). Also
// includes month names/abbreviations so dates ("5 Jan 2005") are never touched,
// and short words that collide with trig-function abbreviations (can/Jan → tan).
const FUZZY_STOPWORDS = new Set(['of', 'is', 'in', 'to', 'for', 'and', 'at', 'a', 'an', 'the',
    'on', 'by', 'ka', 'ki', 'ke', 'kya', 'hai', 'se', 'tak', 'kitna', 'kitni',
    'can', 'you', 'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
    'january', 'february', 'march', 'april', 'june', 'july', 'august', 'september', 'october', 'november', 'december']);

// Fuzzy-corrects only words that are (a) not a stopword, (b) not already an
// exact match to DOMAIN_VOCAB/DYNAMIC_ALIASES, and (c) genuinely close to a
// known domain term. Only words of 6+ letters are ever touched — regression
// testing showed shorter words (e.g. "can", "roots", "Jan") are too easy to
// confuse with unrelated domain terms ("tan", "root") at edit distance 1, so
// short words are deliberately left to the existing, explicit SPELL_MAP instead.
// This runs BEFORE the existing normalize()/spellCorrect(), so correctly
// spelled input — and anything already handled by SPELL_MAP — passes through
// completely untouched.
function fuzzyPreprocess(raw) {
    if (!raw) return raw;
    return raw.replace(/\b([a-zA-Z]{6,})\b/g, (word) => {
        const low = word.toLowerCase();
        if (FUZZY_STOPWORDS.has(low)) return word;
        if (DYNAMIC_ALIASES[low]) return DYNAMIC_ALIASES[low];
        if (DOMAIN_VOCAB.includes(low)) return word; // already correct
        const maxDist = low.length <= 7 ? 1 : 2;
        let bestTerm = null, bestDist = maxDist + 1;
        for (const term of DOMAIN_VOCAB) {
            if (term.length < 6) continue; // never target a short vocab word from a fuzzy match
            if (Math.abs(term.length - low.length) > maxDist) continue;
            const d = levenshtein(low, term);
            if (d < bestDist) { bestDist = d; bestTerm = term; }
        }
        return bestDist <= maxDist ? bestTerm : word;
    });
}

/* ---- Session memory: remembers the last meaningful numeric entity so
   short follow-up queries ("ab isme 10% discount lagao") can resolve
   without repeating the full amount. Only ever consulted when a query
   has NO numbers of its own — every currently-working query already
   supplies its own numbers, so this branch is never reached for them. */
const SESSION_MEMORY = { lastAmount: null, lastResultType: null };
const CONTINUATION_WORDS = /\b(isme|ismein|usme|usmein|iska|uska|ab|isse|usse|that|it|same|previous|pichla|pichhla)\b/;
function rememberEntity(nums, resultType) {
    if (nums && nums.length) {
        SESSION_MEMORY.lastAmount = Math.max(...nums);
        SESSION_MEMORY.lastResultType = resultType || SESSION_MEMORY.lastResultType;
    }
}
function getSessionMemory() { return { ...SESSION_MEMORY }; }
function clearSessionMemory() { SESSION_MEMORY.lastAmount = null; SESSION_MEMORY.lastResultType = null; }

/* ---- Small LRU-style result cache. Repeated/duplicate queries (common in
   a chat UI when a user re-taps a suggestion pill) resolve instantly instead
   of re-running the classifier. Skipped for async (live currency) results
   since those depend on live, time-varying exchange rates. */
const RESULT_CACHE = new Map();
const CACHE_MAX = 200;
function cacheGet(key) { return RESULT_CACHE.has(key) ? RESULT_CACHE.get(key) : null; }
function cacheSet(key, value) {
    if (RESULT_CACHE.size >= CACHE_MAX) RESULT_CACHE.delete(RESULT_CACHE.keys().next().value);
    RESULT_CACHE.set(key, value);
}

/* ---- Autocomplete / smart suggestions. Builds a searchable corpus from
   every module's own example hints (the "e.g. '...'" strings already
   present in each Clarify() call), so it stays in sync automatically as
   modules are added — no manual list to maintain. Ranked by prefix match
   first, then fuzzy closeness. Exposed on AIEngine; not wired into the
   current UI (adding a dropdown would change the UI, which is out of scope
   here) but ready for future use. */
let _autocompleteCorpus = null;
function buildAutocompleteCorpus() {
    const examples = new Set();
    MODULES.forEach(m => { if (m.name) examples.add(m.name); });
    // Harvest "e.g. '...'" example phrases straight out of the Clarify hints
    // already written into every module above.
    const src = MODULES.map(m => m.run.toString()).join('\n');
    const hintMatches = src.match(/e\.g\.\s*'([^']+)'/g) || [];
    hintMatches.forEach(h => {
        const m = h.match(/e\.g\.\s*'([^']+)'/);
        if (m) examples.add(m[1]);
    });
    return Array.from(examples);
}
function autocomplete(prefix, limit = 6) {
    if (!prefix || !prefix.trim()) return [];
    if (!_autocompleteCorpus) _autocompleteCorpus = buildAutocompleteCorpus();
    const low = prefix.toLowerCase().trim();
    const starts = [], contains = [], fuzzy = [];
    for (const phrase of _autocompleteCorpus) {
        const p = phrase.toLowerCase();
        if (p.startsWith(low)) starts.push(phrase);
        else if (p.includes(low)) contains.push(phrase);
        else if (low.length >= 4 && levenshtein(low, p.slice(0, low.length)) <= 2) fuzzy.push(phrase);
    }
    return [...starts, ...contains, ...fuzzy].slice(0, limit);
}

/* ---- Module keyword registry for future scalability. At 40 modules a
   full linear scan (as used below) is sub-millisecond and needs no index.
   This registry lets FUTURE modules (toward the requested 500+ scale)
   opt into fast keyword pre-filtering without changing how the current
   40 modules are matched. */
const MODULE_KEYWORD_INDEX = {};
function registerModuleKeywords(moduleId, keywords) {
    MODULE_KEYWORD_INDEX[moduleId] = (keywords || []).map(k => k.toLowerCase());
}

/* ---------------------------------------------------------------
   ROUTER
   --------------------------------------------------------------- */
function classify(rawQuery) {
    // ENHANCEMENT: cache lookup — instant response for a repeated/duplicate query
    // (e.g. re-tapping the same suggestion pill). Async modules are never cached
    // (see cacheSet call below), so live currency rates are unaffected.
    const cacheKey = String(rawQuery || '').trim().toLowerCase();
    if (cacheKey) {
        const cached = cacheGet(cacheKey);
        // A cache hit must still refresh session memory (a repeated query is still
        // the "most recent" amount used) — otherwise a later follow-up query could
        // silently lose context purely because this exact query was seen before.
        if (cached) {
            if (cached.response && cached.response.mode === 'result' && cached._memNums) {
                rememberEntity(cached._memNums, cached.intentId);
            }
            return cached;
        }
    }

    // ENHANCEMENT: typo correction runs BEFORE the existing normalize() below.
    // For already-correctly-spelled input this is a no-op passthrough, so every
    // query that worked before still flows through normalize()/classify() exactly
    // as it did previously.
    const preprocessed = fuzzyPreprocess(rawQuery);

    // ---- ORIGINAL CORE LOGIC (unmodified) ----
    const q = normalize(preprocessed);
    const nums = allNums(q);
    let best = null, bestScore = 0;
    const alternates = []; // ENHANCEMENT: multi-intent tracking, collected in the same loop — no extra scan
    for (const mod of MODULES) {
        let s = 0;
        try { s = mod.score(q, nums); } catch (e) { s = 0; }
        if (s > 0) alternates.push({ id: mod.id, name: mod.name, score: s });
        if (s > bestScore) { bestScore = s; best = mod; }
        // ENHANCEMENT: safe early-exit. Only smallTalk ever scores 0.99+ in this
        // file, and it is the first module pushed (index 0), so breaking here
        // can never skip past a module that would otherwise have won — it only
        // saves scanning the remaining ~39 modules for common greetings.
        if (s >= 0.99) break;
    }
    // ---- END ORIGINAL CORE LOGIC ----

    alternates.sort((a, b) => b.score - a.score);
    const topAlternates = alternates.filter(a => !best || a.id !== best.id).slice(0, 2)
        .map(a => ({ id: a.id, name: a.name, score: Math.round(a.score * 100) / 100 }));

    // ENHANCEMENT: session-memory fallback. Only kicks in when the query has NO
    // numbers of its own AND uses a continuation word ("isme", "uska", "ab", "it").
    // Every currently-working query already supplies its own numbers (nums.length
    // > 0), so this can never alter an already-successful classification — it only
    // gives previously-unresolvable follow-ups a chance to resolve.
    let effectiveNums = nums;
    if (nums.length === 0 && best && CONTINUATION_WORDS.test(q) && SESSION_MEMORY.lastAmount !== null) {
        effectiveNums = [SESSION_MEMORY.lastAmount];
    }

    if (!best || bestScore === 0) {
        // ===== NEW FEATURE START ===== (varied message + "did you mean" nudge)
        const nearMatches = autocomplete(rawQuery, 3).filter(
            s => s.toLowerCase() !== String(rawQuery || '').trim().toLowerCase()
        );
        // ===== NEW FEATURE END =====
        const result = {
            intentId: null, response: {
                mode: 'unknown',
                message: pick(FALLBACK_MESSAGES), // ===== NEW FEATURE =====
                suggestions: ['15% of 2500', 'emi for 5 lakh at 8.5% for 5 years', 'bmi 70kg 170cm', 'what can you do'],
                didYouMean: nearMatches.length ? nearMatches : undefined, // ===== NEW FEATURE =====
            },
            confidence: 0,           // ENHANCEMENT: confidence score
            alternates: topAlternates, // ENHANCEMENT: multi-intent candidates
        };
        // ===== NEW FEATURE ===== not cached: `message`/`didYouMean` are randomised
        // per call (pick() + fuzzy nearMatches), so caching would freeze the same
        // reply forever for a repeated query — the opposite of "feels less robotic".
        return result;
    }
    let out;
    try { out = best.run(q, effectiveNums); } catch (e) { out = 0; }

    // ENHANCEMENT: if the module still couldn't complete (e.g. "18% gst on that"
    // — has a rate but no base amount) AND this looks like a follow-up AND we
    // have a remembered amount not already in the number list, retry once with
    // it appended. Only reached when the original attempt was already a
    // clarify/unknown, so a previously-working query is never affected.
    if (out && out.mode === 'clarify' && CONTINUATION_WORDS.test(q)
        && SESSION_MEMORY.lastAmount !== null && !effectiveNums.includes(SESSION_MEMORY.lastAmount)) {
        let retryOut;
        try { retryOut = best.run(q, [...effectiveNums, SESSION_MEMORY.lastAmount]); } catch (e) { retryOut = 0; }
        if (retryOut && retryOut.mode === 'result') { out = retryOut; effectiveNums = [...effectiveNums, SESSION_MEMORY.lastAmount]; }
    }

    if (!out) {
        // ===== NEW FEATURE START =====
        const nearMatches2 = autocomplete(rawQuery, 3).filter(
            s => s.toLowerCase() !== String(rawQuery || '').trim().toLowerCase()
        );
        // ===== NEW FEATURE END =====
        const result = {
            intentId: best.id, response: {
                mode: 'unknown',
                message: pick(FALLBACK_MESSAGES), // ===== NEW FEATURE =====
                suggestions: ['15% of 2500', 'emi for 5 lakh at 8.5% for 5 years', 'bmi 70kg 170cm'],
                didYouMean: nearMatches2.length ? nearMatches2 : undefined, // ===== NEW FEATURE =====
            },
            confidence: bestScore,
            alternates: topAlternates,
        };
        // ===== NEW FEATURE ===== not cached — same reason as above (randomised message)
        return result;
    }
    if (out.mode === 'result') rememberEntity(effectiveNums, best.id); // ENHANCEMENT: session memory update

    const finalResult = { intentId: best.id, response: out, isAsync: !!best.async, confidence: bestScore, alternates: topAlternates, _memNums: effectiveNums };
    // ===== NEW FEATURE ===== also skip caching 'chat' responses — smallTalk replies
    // use pick() for variety (jokes, greetings, ...), so caching would freeze the
    // first random reply forever for a repeated query like "tell me a joke".
    if (cacheKey && !best.async && out.mode !== 'chat') cacheSet(cacheKey, finalResult);
    return finalResult;
}

function retryWithContext(intentId, rawQuery) {
    const mod = MODULES.find(m => m.id === intentId);
    if (!mod) return classify(rawQuery);
    // ENHANCEMENT: same safe, no-op-for-correct-input typo correction as classify()
    const preprocessed = fuzzyPreprocess(rawQuery);
    const q = normalize(preprocessed);
    const nums = allNums(q);
    let out;
    try { out = mod.run(q, nums); } catch (e) { out = 0; }
    if (!out) return classify(rawQuery);
    if (out.mode === 'result') rememberEntity(nums, mod.id); // ENHANCEMENT: session memory update
    return { intentId: mod.id, response: out, isAsync: !!mod.async };
}

/* ---------------------------------------------------------------
   ENHANCEMENT: classifyDetailed() — opt-in richer entry point for
   consumers that want confidence + full alternates without changing
   the default classify() return shape relied on by the existing UI.
   --------------------------------------------------------------- */
function classifyDetailed(rawQuery) {
    const out = classify(rawQuery);
    return {
        ...out,
        confidence: out.confidence ?? (out.intentId ? 1 : 0),
        alternates: out.alternates || [],
    };
}

/* ===== NEW FEATURE START =====================================================
   PLUGIN LAYER — Related Calculators + Learning Links.

   Nothing above this block is modified. classify() and retryWithContext()
   above run 100% unchanged; these wrappers call them as-is and only ADD
   extra, optional fields to the response object afterward:
     response.relatedCalculators  (array | absent)
     response.learningLinks       (array | absent)

   This is intentionally structured as a removable plugin: the exported
   `classify`/`retryWithContext` below simply point at these wrappers instead
   of the originals. To disable the plugin entirely, change the two lines in
   the AIEngine export at the bottom of this file back to the plain
   `classify`/`retryWithContext` names (still exported as classifyCore /
   retryWithContextCore) — nothing else needs to change.
   =============================================================================== */

// Maps this engine's internal module ids -> relatedCalculators.js top-level keys.
// Only modules that have a genuine match are listed; anything not listed here
// (e.g. cagr, roi, unitConvert, ...) simply gets no "Related Calculators"
// section, per the "if no related calculators exist, show nothing" rule.
const MODULE_TO_RELATED_KEY = {
    percentage: 'percentage',
    gst: 'gst',
    discount: 'discount',
    emi: 'emi',
    compoundInterest: 'interest',
    simpleInterest: 'interest',
    sip: 'sip',
    bmi: 'bmi',
    calories: 'calorie', // this engine's single "calories" module covers BMR + calories;
                          // mapped to "calorie" whose own related list (bmr, bmi) still
                          // surfaces a BMR suggestion, so nothing is lost.
    age: 'age',
    dateDiff: 'date-difference',
};

// Reads relatedCalculators.js ONLY (never hardcodes a link here) and resolves
// a module's sibling suggestions into full {title, url, category} objects.
function attachRelatedCalculators(intentId) {
    const key = MODULE_TO_RELATED_KEY[intentId];
    if (!key) return [];
    const entry = relatedCalculators[key];
    if (!entry || !Array.isArray(entry.related) || !entry.related.length) return [];
    return entry.related
        .map(siblingKey => relatedCalculators[siblingKey])
        .filter(Boolean)
        .map(sibling => ({ title: sibling.title, url: sibling.url, category: sibling.category }));
}

// Keyword -> learningLinks.js key, for topics that name a specific technology.
const LEARNING_KEYWORD_MAP = [
    [/\bexcel\b/i, 'excel'],
    [/\bpython\b/i, 'python'],
    [/\bsql\b/i, 'sql'],
];
// Broader learning-intent words from the spec (learning, education, course,
// tutorial, programming, ai, power bi, ms office, data analytics, coding,
// interview preparation) that don't name a specific tech covered above.
const GENERIC_LEARNING_RE = /\b(learn(ing)?|education|course|tutorial|programming|coding|interview\s*preparation|power\s*bi|ms\s*office|data\s*analytics)\b/i;

// Detects whether the user's raw query is about learning/education at all,
// and if so which learningLinks.js key (if any) it maps to. Returns:
//   a specific key ('excel' | 'python' | 'sql')  -> that topic was named
//   'general'                                    -> learning intent, no specific tech named
//   null                                         -> not a learning-related query
function detectLearningTopic(rawQuery) {
    if (!rawQuery) return null;
    for (const [re, key] of LEARNING_KEYWORD_MAP) {
        if (re.test(rawQuery)) return key;
    }
    if (GENERIC_LEARNING_RE.test(rawQuery)) return 'general';
    return null;
}

// Reads learningLinks.js ONLY (never an external/hardcoded URL). For a
// specific tech, returns that tech's own links. For a generic "I want to
// learn" query with no named tech, gives a short taste of everything
// available so the user can pick a track — still exclusively from this file.
function attachLearningLinks(topic) {
    if (!topic) return [];
    if (topic === 'general') return Object.values(learningLinks).flatMap(arr => arr.slice(0, 2));
    return learningLinks[topic] || [];
}

// Enriches a single response object (result / clarify / chat / unknown — any
// mode) with the two optional fields, then returns a NEW object. The
// original response object passed in is never mutated.
function enrichResponse(response, intentId, rawQuery) {
    if (!response || typeof response !== 'object') return response;
    const enriched = { ...response };

    // FEATURE 1: Related Calculators — only after a successful calculation.
    if (response.mode === 'result') {
        const rc = attachRelatedCalculators(intentId);
        if (rc.length) enriched.relatedCalculators = rc;
    }

    // FEATURE 2: Learning Links — independent of mode, based on the user's
    // raw query text (works alongside a calculator result, a chat reply, or
    // an unknown-query response).
    const topic = detectLearningTopic(rawQuery);
    const links = attachLearningLinks(topic);
    if (links.length) enriched.learningLinks = links;

    return enriched;
}

// Drop-in replacement for classify(): calls the original, untouched
// classify() and only adds the two optional fields above. Handles the async
// (live currency) case by enriching after the promise resolves, so the
// existing async flow in the UI is unaffected.
function classifyWithPlugins(rawQuery) {
    const outcome = classify(rawQuery);
    if (outcome.isAsync) {
        return {
            ...outcome,
            response: Promise.resolve(outcome.response).then(resolved => enrichResponse(resolved, outcome.intentId, rawQuery)),
        };
    }
    return { ...outcome, response: enrichResponse(outcome.response, outcome.intentId, rawQuery) };
}

// Drop-in replacement for retryWithContext(): same enrichment, same pattern.
function retryWithContextPlugins(intentId, rawQuery) {
    const outcome = retryWithContext(intentId, rawQuery);
    if (outcome.isAsync) {
        return {
            ...outcome,
            response: Promise.resolve(outcome.response).then(resolved => enrichResponse(resolved, outcome.intentId, rawQuery)),
        };
    }
    return { ...outcome, response: enrichResponse(outcome.response, outcome.intentId, rawQuery) };
}
/* ===== NEW FEATURE END ===== */

// AIEngine export — all original keys (classify, retryWithContext, normalize,
// allNums) are preserved exactly as before. New keys are additive only.
const AIEngine = {
    // ===== NEW FEATURE START =====
    // classify/retryWithContext now point at the plugin-wrapped versions.
    // The untouched originals are still fully available under *Core names —
    // switch these two lines back to `classify, retryWithContext,` to remove
    // the plugin layer instantly without deleting anything.
    classify: classifyWithPlugins, retryWithContext: retryWithContextPlugins,
    classifyCore: classify, retryWithContextCore: retryWithContext,
    // ===== NEW FEATURE END =====
    normalize, allNums,
    // ---- new, additive capabilities ----
    classifyDetailed,       // confidence + multi-intent alternates
    autocomplete,           // smart suggestions / auto-complete
    addAlias,                // register a dynamic synonym at runtime
    registerModuleKeywords,  // opt-in fast keyword index for future modules
    getSessionMemory,        // inspect current session/context memory
    clearSessionMemory,      // reset session/context memory
    levenshtein,             // exposed for advanced "did you mean" UIs
};
export default AIEngine;
